import React, { useState, useEffect } from 'react';
import { useAuth, useUser, SignIn } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function proxyImage(url) {
  if (!url) return null;
  return url; // load directly from Instagram CDN
}


function recipeEmoji(recipe) {
  const text = ((recipe.title || '') + ' ' + (recipe.tags || []).join(' ')).toLowerCase();
  if (text.match(/chicken|poultry/)) return '🍗';
  if (text.match(/burger|smash/)) return '🍔';
  if (text.match(/pasta|spaghetti|carbonara|lasagne/)) return '🍝';
  if (text.match(/cake|brownie|biscuit|cookie|bake|dessert|sweet/)) return '🍰';
  if (text.match(/chocolate|cocoa/)) return '🍫';
  if (text.match(/salad/)) return '🥗';
  if (text.match(/soup|stew/)) return '🍲';
  if (text.match(/pizza/)) return '🍕';
  if (text.match(/taco|burrito|mexican/)) return '🌮';
  if (text.match(/sushi|japanese/)) return '🍣';
  if (text.match(/curry|indian/)) return '🍛';
  if (text.match(/fish|salmon|cod|prawn|seafood/)) return '🐟';
  if (text.match(/egg|omelette|frittata/)) return '🍳';
  if (text.match(/oat|breakfast|granola/)) return '🥣';
  if (text.match(/smoothie|juice|drink/)) return '🥤';
  if (text.match(/bread|toast|bagel/)) return '🍞';
  if (text.match(/steak|beef|mince/)) return '🥩';
  if (text.match(/pork|bacon|ham/)) return '🥓';
  if (text.match(/veg|vegetable|vegan|plant/)) return '🥦';
  return '🍽';
}


function matchesSearch(recipe, q) {
  const s = q.toLowerCase();
  return recipe.title?.toLowerCase().includes(s) ||
    (recipe.tags||[]).some(t => t.toLowerCase().includes(s)) ||
    (recipe.ingredients||[]).some(i => i.item?.toLowerCase().includes(s)) ||
    recipe.creatorUsername?.toLowerCase().includes(s) ||
    recipe.creatorDisplayName?.toLowerCase().includes(s);
}

function getCreators(recipes) {
  const map = {};
  recipes.forEach(r => {
    // Only include recipes that have a real creator username
    if (!r.creatorUsername) return;
    const key = r.creatorUsername;
    if (!map[key]) {
      map[key] = {
        username: r.creatorUsername,
        displayName: r.creatorDisplayName,
        avatarUrl: r.creatorAvatarUrl,
        recipes: [],
      };
    }
    // Use best available avatar/displayName across recipes from same creator
    if (!map[key].avatarUrl && r.creatorAvatarUrl) map[key].avatarUrl = r.creatorAvatarUrl;
    if (!map[key].displayName && r.creatorDisplayName) map[key].displayName = r.creatorDisplayName;
    map[key].recipes.push(r);
  });
  return Object.values(map).sort((a,b) => b.recipes.length - a.recipes.length);
}

function scaleIngredient(amount, baseServings, currentServings) {
  if (!amount || !baseServings || baseServings === currentServings) return amount;
  const num = parseFloat(String(amount));
  if (isNaN(num)) return amount;
  const scaled = (num * currentServings) / baseServings;
  // Nice fractions
  if (scaled === Math.floor(scaled)) return String(scaled);
  const rounded = Math.round(scaled * 4) / 4;
  if (rounded === Math.floor(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/\.?0+$/, '');
}

// ─── Logo SVGs ────────────────────────────────────────────────────────────────

// Single full logo — icon + wordmark combined SVG (scales by height, width auto)
function FullLogo({ height = 22, iconOnly = false }) {
  if (iconOnly) {
    // Just the icon portion (viewBox 0 0 25 25)
    return (
      <svg width={height} height={height} viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.7134 8.12811L20.4668 8.69379C20.2864 9.10792 19.7136 9.10792 19.5331 8.69379L19.2866 8.12811C18.8471 7.11947 18.0555 6.31641 17.0677 5.87708L16.308 5.53922C15.8973 5.35653 15.8973 4.75881 16.308 4.57612L17.0252 4.25714C18.0384 3.80651 18.8442 2.97373 19.2761 1.93083L19.5293 1.31953C19.7058 0.89349 20.2942 0.89349 20.4706 1.31953L20.7238 1.93083C21.1558 2.97373 21.9616 3.80651 22.9748 4.25714L23.6919 4.57612C24.1027 4.75881 24.1027 5.35653 23.6919 5.53922L22.9323 5.87708C21.9445 6.31641 21.1529 7.11947 20.7134 8.12811ZM2.9918 3H14V5H4V19H20V11H22V20.0066C22 20.5552 21.5447 21 21.0082 21H2.9918C2.44405 21 2 20.5551 2 20.0066V3.9934C2 3.44476 2.45531 3 2.9918 3ZM10.6219 8.41459L15.5008 11.6672C15.6846 11.7897 15.7343 12.0381 15.6117 12.2219C15.5824 12.2658 15.5447 12.3035 15.5008 12.3328L10.6219 15.5854C10.4381 15.708 10.1897 15.6583 10.0672 15.4745C10.0234 15.4088 10 15.3316 10 15.2526V8.74741C10 8.52649 10.1791 8.34741 10.4 8.34741C10.479 8.34741 10.5562 8.37078 10.6219 8.41459Z" fill="#E8845A"/>
      </svg>
    );
  }
  const w = Math.round(height * (132 / 25));
  return (
    <svg width={w} height={height} viewBox="0 0 132 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.7134 8.12811L20.4668 8.69379C20.2864 9.10792 19.7136 9.10792 19.5331 8.69379L19.2866 8.12811C18.8471 7.11947 18.0555 6.31641 17.0677 5.87708L16.308 5.53922C15.8973 5.35653 15.8973 4.75881 16.308 4.57612L17.0252 4.25714C18.0384 3.80651 18.8442 2.97373 19.2761 1.93083L19.5293 1.31953C19.7058 0.89349 20.2942 0.89349 20.4706 1.31953L20.7238 1.93083C21.1558 2.97373 21.9616 3.80651 22.9748 4.25714L23.6919 4.57612C24.1027 4.75881 24.1027 5.35653 23.6919 5.53922L22.9323 5.87708C21.9445 6.31641 21.1529 7.11947 20.7134 8.12811ZM2.9918 3H14V5H4V19H20V11H22V20.0066C22 20.5552 21.5447 21 21.0082 21H2.9918C2.44405 21 2 20.5551 2 20.0066V3.9934C2 3.44476 2.45531 3 2.9918 3ZM10.6219 8.41459L15.5008 11.6672C15.6846 11.7897 15.7343 12.0381 15.6117 12.2219C15.5824 12.2658 15.5447 12.3035 15.5008 12.3328L10.6219 15.5854C10.4381 15.708 10.1897 15.6583 10.0672 15.4745C10.0234 15.4088 10 15.3316 10 15.2526V8.74741C10 8.52649 10.1791 8.34741 10.4 8.34741C10.479 8.34741 10.5562 8.37078 10.6219 8.41459Z" fill="#E8845A"/>
      <path d="M59.9091 19.0795V5.69099H62.3572V19.0795H59.9091Z" fill="white"/>
      <path d="M54.0614 19.309C53.0668 19.309 52.2061 19.0922 51.4793 18.6587C50.7653 18.2252 50.2106 17.6323 49.8153 16.88C49.4201 16.1276 49.2224 15.2797 49.2224 14.3361C49.2224 13.3288 49.4264 12.4554 49.8345 11.7158C50.2425 10.9763 50.8099 10.4025 51.5367 9.99444C52.2635 9.57366 53.1051 9.36327 54.0614 9.36327C55.005 9.36327 55.8274 9.56728 56.5287 9.97531C57.2428 10.3833 57.7974 10.9444 58.1927 11.6584C58.6007 12.3725 58.8048 13.1949 58.8048 14.1257C58.8048 14.2915 58.7984 14.4573 58.7856 14.623C58.7729 14.776 58.7538 14.9227 58.7283 15.0629H51.6897C51.728 15.5092 51.8427 15.9045 52.034 16.2488C52.238 16.5803 52.5058 16.8417 52.8373 17.033C53.1816 17.2115 53.5705 17.3007 54.004 17.3007C54.4503 17.3007 54.8328 17.2051 55.1516 17.0138C55.4831 16.8226 55.7445 16.5293 55.9358 16.134H58.5561C58.3776 16.6951 58.0907 17.2179 57.6954 17.7024C57.3001 18.1869 56.7965 18.5758 56.1844 18.8691C55.5851 19.1624 54.8775 19.309 54.0614 19.309ZM51.7088 13.4372H56.2801C56.2673 12.7996 56.0314 12.296 55.5724 11.9262C55.1261 11.5564 54.5969 11.3715 53.9849 11.3715C53.4621 11.3715 52.9776 11.5373 52.5313 11.8688C52.085 12.1876 51.8109 12.7104 51.7088 13.4372Z" fill="white"/>
      <path d="M43.6808 19.309C42.6863 19.309 41.8256 19.0922 41.0988 18.6587C40.3847 18.2252 39.8301 17.6323 39.4348 16.88C39.0395 16.1276 38.8419 15.2797 38.8419 14.3361C38.8419 13.3288 39.0459 12.4554 39.4539 11.7158C39.8619 10.9763 40.4294 10.4025 41.1562 9.99444C41.883 9.57366 42.7245 9.36327 43.6808 9.36327C44.6244 9.36327 45.4469 9.56728 46.1482 9.97531C46.8622 10.3833 47.4169 10.9444 47.8122 11.6584C48.2202 12.3725 48.4242 13.1949 48.4242 14.1257C48.4242 14.2915 48.4178 14.4573 48.4051 14.623C48.3923 14.776 48.3732 14.9227 48.3477 15.0629H41.3092C41.3474 15.5092 41.4622 15.9045 41.6534 16.2488C41.8575 16.5803 42.1252 16.8417 42.4568 17.033C42.801 17.2115 43.1899 17.3007 43.6235 17.3007C44.0698 17.3007 44.4523 17.2051 44.7711 17.0138C45.1026 16.8226 45.364 16.5293 45.5552 16.134H48.1756C47.997 16.6951 47.7102 17.2179 47.3149 17.7024C46.9196 18.1869 46.4159 18.5758 45.8039 18.8691C45.2046 19.1624 44.4969 19.309 43.6808 19.309ZM41.3283 13.4372H45.8995C45.8868 12.7996 45.6509 12.296 45.1918 11.9262C44.7456 11.5564 44.2164 11.3715 43.6043 11.3715C43.0816 11.3715 42.597 11.5373 42.1507 11.8688C41.7044 12.1876 41.4303 12.7104 41.3283 13.4372Z" fill="white"/>
      <path d="M29 19.0795V5.69099H33.6477C34.5785 5.69099 35.3819 5.84401 36.0577 6.15003C36.7462 6.4433 37.2817 6.87683 37.6643 7.45063C38.0468 8.02442 38.2381 8.72572 38.2381 9.55454C38.2381 10.5109 37.983 11.2759 37.473 11.8497C36.963 12.4235 36.268 12.8188 35.3882 13.0355L39.1179 19.0795H36.2298L32.94 13.4372H31.4482V19.0795H29ZM31.4482 11.3524H33.5903C34.1641 11.3524 34.604 11.2759 34.9101 11.1229C35.2288 10.9571 35.4456 10.7404 35.5604 10.4726C35.6879 10.2048 35.7516 9.89881 35.7516 9.55454C35.7516 9.21026 35.6879 8.90424 35.5604 8.63647C35.4456 8.3687 35.2288 8.1583 34.9101 8.00529C34.604 7.85228 34.1641 7.77578 33.5903 7.77578H31.4482V11.3524Z" fill="white"/>
      <path d="M63.7726 19V5.61149H67.7318C68.6753 5.61149 69.4786 5.75175 70.1417 6.03228C70.8047 6.30005 71.3148 6.70808 71.6718 7.25637C72.0288 7.80466 72.2074 8.49959 72.2074 9.34115C72.2074 10.3867 71.9332 11.1773 71.3849 11.7128C70.8494 12.2484 70.1162 12.599 69.1854 12.7648L73.0298 19H71.0406L67.5979 13.0708H65.3792V19H63.7726ZM65.3792 11.7128H67.6935C68.4076 11.7128 68.975 11.6299 69.3958 11.4642C69.8165 11.2857 70.1162 11.0179 70.2947 10.6609C70.4732 10.3038 70.5625 9.86394 70.5625 9.34115C70.5625 8.80561 70.4732 8.35933 70.2947 8.0023C70.1162 7.64527 69.8165 7.38388 69.3958 7.21811C68.975 7.0396 68.4076 6.95034 67.6935 6.95034H65.3792V11.7128ZM78.5095 19.2295C77.6297 19.2295 76.8328 19.0255 76.1187 18.6175C75.4174 18.2094 74.8627 17.6356 74.4547 16.8961C74.0467 16.1438 73.8427 15.264 73.8427 14.2566C73.8427 13.2621 74.0339 12.395 74.4165 11.6554C74.8117 10.9031 75.36 10.323 76.0613 9.91494C76.7754 9.49416 77.6042 9.28377 78.5478 9.28377C79.4403 9.28377 80.2181 9.48141 80.8812 9.87669C81.5442 10.272 82.0607 10.8075 82.4304 11.4833C82.813 12.1464 83.0042 12.8923 83.0042 13.7211C83.0042 13.8996 83.0042 14.0399 83.0042 14.1419C83.0042 14.2439 82.9979 14.4033 82.9851 14.62H75.4302C75.4684 15.2448 75.615 15.8059 75.8701 16.3032C76.1378 16.7877 76.4949 17.1702 76.9411 17.4508C77.4002 17.7313 77.923 17.8715 78.5095 17.8715C79.1726 17.8715 79.7272 17.7185 80.1735 17.4125C80.6198 17.1065 80.9449 16.6921 81.149 16.1693H82.7365C82.5707 16.7558 82.2965 17.2786 81.914 17.7377C81.5315 18.1967 81.0533 18.5601 80.4795 18.8279C79.9185 19.0956 79.2618 19.2295 78.5095 19.2295ZM75.4493 13.3768H81.4167C81.3785 12.5225 81.0852 11.8531 80.5369 11.3686C79.9886 10.884 79.3128 10.6417 78.5095 10.6417C77.7445 10.6417 77.0623 10.8776 76.463 11.3494C75.8764 11.8085 75.5385 12.4843 75.4493 13.3768ZM89.3032 19.2295C88.4107 19.2295 87.601 19.0255 86.8742 18.6175C86.1601 18.2094 85.5927 17.6356 85.1719 16.8961C84.7512 16.1438 84.5408 15.264 84.5408 14.2566C84.5408 13.2748 84.7448 12.4141 85.1528 11.6746C85.5608 10.9223 86.1219 10.3357 86.8359 9.91494C87.55 9.49416 88.3724 9.28377 89.3032 9.28377C90.4253 9.28377 91.3689 9.57704 92.134 10.1636C92.9118 10.7501 93.4027 11.5343 93.6067 12.5161H91.9618C91.8343 11.9296 91.5219 11.4769 91.0246 11.1582C90.5273 10.8266 89.9472 10.6609 89.2841 10.6609C88.6083 10.6609 88.0409 10.8266 87.5819 11.1582C87.1228 11.4897 86.7722 11.9296 86.5299 12.4779C86.3004 13.0262 86.1856 13.6191 86.1856 14.2566C86.1856 14.9579 86.3195 15.5827 86.5873 16.131C86.8551 16.6793 87.2185 17.1065 87.6775 17.4125C88.1493 17.7185 88.6848 17.8715 89.2841 17.8715C89.9472 17.8715 90.5273 17.7122 91.0246 17.3934C91.5219 17.0619 91.8343 16.5964 91.9618 15.9971H93.6067C93.4792 16.6347 93.2178 17.1957 92.8225 17.6803C92.4272 18.1648 91.9299 18.5473 91.3307 18.8279C90.7314 19.0956 90.0556 19.2295 89.3032 19.2295ZM96.6347 7.40938C96.3159 7.40938 96.0481 7.30737 95.8313 7.10336C95.6273 6.88659 95.5253 6.61882 95.5253 6.30005C95.5253 5.99402 95.6273 5.739 95.8313 5.53499C96.0481 5.33097 96.3159 5.22897 96.6347 5.22897C96.9407 5.22897 97.2021 5.33097 97.4188 5.53499C97.6356 5.739 97.744 5.99402 97.744 6.30005C97.744 6.61882 97.6356 6.88659 97.4188 7.10336C97.2021 7.30737 96.9407 7.40938 96.6347 7.40938ZM95.8313 19V9.51329H97.438V19H95.8313ZM100.044 23.2078V9.51329H101.498L101.651 11.0817C101.957 10.6226 102.39 10.2082 102.951 9.83844C103.525 9.46866 104.252 9.28377 105.132 9.28377C106.088 9.28377 106.917 9.50054 107.618 9.93407C108.319 10.3676 108.861 10.9605 109.244 11.7128C109.626 12.4651 109.818 13.3194 109.818 14.2758C109.818 15.2193 109.626 16.0673 109.244 16.8196C108.861 17.5719 108.313 18.1648 107.599 18.5983C106.898 19.0191 106.069 19.2295 105.113 19.2295C104.335 19.2295 103.64 19.0701 103.028 18.7514C102.429 18.4326 101.969 17.9863 101.651 17.4125V23.2078H100.044ZM104.94 17.8333C105.591 17.8333 106.158 17.6867 106.643 17.3934C107.127 17.0874 107.503 16.6666 107.771 16.131C108.052 15.5827 108.192 14.9579 108.192 14.2566C108.192 13.5808 108.058 12.9752 107.79 12.4396C107.535 11.8913 107.165 11.4642 106.681 11.1582C106.196 10.8394 105.616 10.68 104.94 10.68C104.303 10.68 103.735 10.833 103.238 11.139C102.754 11.4323 102.371 11.8467 102.091 12.3823C101.823 12.9178 101.689 13.5426 101.689 14.2566C101.689 14.9197 101.823 15.5254 102.091 16.0737C102.358 16.6092 102.735 17.0364 103.219 17.3551C103.704 17.6739 104.277 17.8333 104.94 17.8333ZM116.128 19.2295C115.248 19.2295 114.451 19.0255 113.737 18.6175C113.036 18.2094 112.481 17.6356 112.073 16.8961C111.665 16.1438 111.461 15.264 111.461 14.2566C111.461 13.2621 111.652 12.395 112.035 11.6554C112.43 10.9031 112.979 10.323 113.68 9.91494C114.394 9.49416 115.223 9.28377 116.166 9.28377C117.059 9.28377 117.837 9.48141 118.5 9.87669C119.163 10.272 119.679 10.8075 120.049 11.4833C120.432 12.1464 120.623 12.8923 120.623 13.7211C120.623 13.8996 120.623 14.0399 120.623 14.1419C120.623 14.2439 120.616 14.4033 120.604 14.62H113.049C113.087 15.2448 113.234 15.8059 113.489 16.3032C113.756 16.7877 114.113 17.1702 114.56 17.4508C115.019 17.7313 115.542 17.8715 116.128 17.8715C116.791 17.8715 117.346 17.7185 117.792 17.4125C118.238 17.1065 118.564 16.6921 118.768 16.1693H120.355C120.189 16.7558 119.915 17.2786 119.533 17.7377C119.15 18.1967 118.672 18.5601 118.098 18.8279C117.537 19.0956 116.88 19.2295 116.128 19.2295ZM113.068 13.3768H119.035C118.997 12.5225 118.704 11.8531 118.155 11.3686C117.607 10.884 116.931 10.6417 116.128 10.6417C115.363 10.6417 114.681 10.8776 114.082 11.3494C113.495 11.8085 113.157 12.4843 113.068 13.3768ZM126.195 19.2295C125.443 19.2295 124.767 19.102 124.168 18.847C123.568 18.5792 123.084 18.2094 122.714 17.7377C122.357 17.2531 122.134 16.6857 122.045 16.0354H123.689C123.791 16.5327 124.053 16.9662 124.474 17.336C124.907 17.693 125.487 17.8715 126.214 17.8715C126.89 17.8715 127.387 17.7313 127.706 17.4508C128.025 17.1575 128.184 16.8132 128.184 16.4179C128.184 16.0354 128.089 15.7421 127.897 15.5381C127.719 15.3341 127.451 15.1811 127.094 15.0791C126.75 14.9643 126.323 14.8559 125.812 14.7539C125.277 14.6392 124.754 14.4862 124.244 14.2949C123.734 14.0909 123.313 13.8167 122.982 13.4725C122.65 13.1282 122.484 12.6755 122.484 12.1145C122.484 11.5534 122.625 11.0625 122.905 10.6417C123.186 10.221 123.581 9.88944 124.091 9.64717C124.614 9.4049 125.226 9.28377 125.927 9.28377C126.935 9.28377 127.757 9.53879 128.395 10.0488C129.045 10.5461 129.421 11.2602 129.523 12.191H127.936C127.872 11.7065 127.661 11.3303 127.304 11.0625C126.96 10.782 126.495 10.6417 125.908 10.6417C125.334 10.6417 124.888 10.7629 124.569 11.0051C124.263 11.2474 124.11 11.5662 124.11 11.9615C124.11 12.2165 124.199 12.4333 124.378 12.6118C124.557 12.7903 124.812 12.9433 125.143 13.0708C125.487 13.1983 125.889 13.3131 126.348 13.4151C126.986 13.5426 127.566 13.702 128.089 13.8932C128.611 14.0845 129.032 14.365 129.351 14.7348C129.67 15.0918 129.829 15.6082 129.829 16.284C129.842 16.8451 129.695 17.3488 129.389 17.795C129.096 18.2413 128.675 18.592 128.127 18.847C127.591 19.102 126.947 19.2295 126.195 19.2295Z" fill="white"/>
    </svg>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { height: 100%; }
  body { height: 100%; }
  #root { height: 100%; display: flex; flex-direction: column; }

  :root {
    --cream: #faf7f2;
    --warm-white: #fff9f4;
    --ink: #1a1209;
    --ink-light: #4a3f30;
    --ink-muted: #9a8f80;
    --terracotta: #c4622d;
    --terracotta-light: #e8845a;
    --terracotta-pale: #fdf0e8;
    --sage: #6b7c5e;
    --sage-light: #eef1eb;
    --border: #e8e0d4;
    --shadow: 0 4px 24px rgba(26,18,9,0.08);
    --shadow-lg: 0 8px 40px rgba(26,18,9,0.14);
  }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--cream);
    color: var(--ink);
  }

  /* ── Desktop App Shell ── */
  .desktop-shell {
    display: flex;
    height: 100vh;
    overflow: hidden;
  }

  /* ── Desktop Nav Sidebar ── */
  .desktop-nav {
    background: #111009;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    height: 100vh;
    position: relative;
    transition: width 0.25s cubic-bezier(0.4,0,0.2,1);
    overflow: visible;
    z-index: 50;
    border-right: 1px solid rgba(255,255,255,0.05);
  }
  .desktop-nav.expanded { width: 240px; }
  .desktop-nav.collapsed { width: 56px; }

  /* Nav header */
  .nav-header-expanded {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 16px 18px;
    flex-shrink: 0;
  }
  .desktop-nav.collapsed .nav-header-expanded { display: none; }

  .nav-header-collapsed {
    display: none;
    flex-direction: column; align-items: center;
    padding: 18px 0 14px; gap: 12px;
    flex-shrink: 0;
  }
  .desktop-nav.collapsed .nav-header-collapsed { display: flex; }

  .nav-logo { display: flex; align-items: center; flex: 1; min-width: 0; }

  .nav-toggle-btn {
    width: 26px; height: 26px; border-radius: 6px;
    background: transparent; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: rgba(255,255,255,0.25); transition: all 0.15s; flex-shrink: 0;
  }
  .nav-toggle-btn:hover { background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.7); }
  .nav-toggle-btn svg { width: 15px; height: 15px; }

  /* Nav groups */
  .nav-group { padding: 4px 8px; }
  .nav-group-label {
    font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
    color: rgba(255,255,255,0.2); font-weight: 600; padding: 10px 8px 4px;
    white-space: nowrap; transition: opacity 0.15s;
  }
  .desktop-nav.collapsed .nav-group-label { opacity: 0; height: 0; padding: 0; overflow: hidden; }

  .nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; cursor: pointer; font-size: 13.5px;
    color: rgba(255,255,255,0.45);
    border-radius: 8px; transition: all 0.12s;
    white-space: nowrap; position: relative;
    background: none; border: none;
    width: 100%; text-align: left; font-family: 'DM Sans', sans-serif;
    font-weight: 400; box-sizing: border-box;
  }
  .nav-item:hover { color: rgba(255,255,255,0.85); background: rgba(255,255,255,0.05); }
  .nav-item.active { color: #fff; background: rgba(255,255,255,0.08); font-weight: 500; }
  .nav-item i { font-size: 16px; flex-shrink: 0; opacity: 0.8; line-height: 1; }
  .nav-item.active i { opacity: 1; }

  .nav-item-label { transition: opacity 0.15s; white-space: nowrap; flex: 1; min-width: 0; }
  .desktop-nav.collapsed .nav-item-label { opacity: 0; width: 0; overflow: hidden; flex: none; }
  .desktop-nav.collapsed .nav-group { padding: 2px 0; }
  .desktop-nav.collapsed .nav-item {
    padding: 9px 0;
    justify-content: center;
    width: 56px;
    gap: 0;
    border-radius: 0;
  }

  /* Count badge */
  .nav-count {
    font-size: 10.5px; font-weight: 500;
    background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.4);
    padding: 1px 7px; border-radius: 20px;
    transition: opacity 0.15s; flex-shrink: 0;
  }
  .nav-item.active .nav-count { background: rgba(196,98,45,0.3); color: var(--terracotta-light); }
  .desktop-nav.collapsed .nav-count { display: none; }

  /* Tooltip on collapsed hover */
  .nav-item::after {
    content: attr(data-label);
    position: absolute; left: 56px; top: 50%; transform: translateY(-50%);
    background: rgba(20,15,8,0.96); color: rgba(255,255,255,0.9);
    font-size: 11.5px; padding: 5px 10px; border-radius: 6px;
    white-space: nowrap; pointer-events: none;
    opacity: 0; transition: opacity 0.15s; z-index: 100;
    border: 1px solid rgba(255,255,255,0.08);
  }
  .desktop-nav.collapsed .nav-item:hover::after { opacity: 1; }

  /* Nav divider */
  .nav-divider {
    height: 1px; background: rgba(255,255,255,0.05);
    margin: 4px 8px;
  }
  .desktop-nav.collapsed .nav-divider { margin: 4px 0; }

  .nav-inner-scroll {
    flex: 1; overflow: hidden; padding-top: 4px;
    min-height: 0;
  }
  .desktop-nav.collapsed .nav-inner-scroll { overflow: hidden; }

  .nav-footer {
    margin-top: auto; padding: 6px 0 8px;
    border-top: 1px solid rgba(255,255,255,0.05);
    display: flex; flex-direction: column; flex-shrink: 0;
  }
  .desktop-nav.collapsed .nav-footer { padding: 4px 0 6px; }
  .desktop-nav.collapsed .nav-footer-info { opacity: 0; width: 0; }
  .desktop-nav.collapsed .nav-footer-settings-icon { display: none; }
  .desktop-nav.collapsed .nav-footer-user-btn { justify-content: center; padding: 7px 0; }
  .nav-footer-user {
    display: flex; align-items: center; gap: 10px;
    padding: 6px 10px;
  }
  .nav-footer-avatar {
    width: 28px; height: 28px; border-radius: 50%;
    background: rgba(196,98,45,0.6); display: flex; align-items: center; justify-content: center;
    font-size: 11px; color: white; font-weight: 600; flex-shrink: 0;
    object-fit: cover; border: 1.5px solid rgba(196,98,45,0.4);
  }
  .nav-footer-name { font-size: 12px; color: rgba(255,255,255,0.55); white-space: nowrap; font-weight: 500; }
  .nav-footer-signout { font-size: 10.5px; color: rgba(255,255,255,0.2); margin-top: 1px; cursor: pointer; white-space: nowrap; background: none; border: none; font-family: 'DM Sans', sans-serif; padding: 0; }
  .nav-footer-signout:hover { color: rgba(255,255,255,0.5); }
  .nav-footer-info { overflow: hidden; transition: opacity 0.15s, width 0.15s; }
  .desktop-nav.collapsed .nav-footer-info { opacity: 0; width: 0; }

  /* ── Dark mode for content area ── */
  /* Only the recipe list sidebar and recipe detail panel go dark — not the home screen */
  .desktop-content.dark-mode {
    background: #1a1512;
  }
  .desktop-content.dark-mode .sidebar {
    background: #1c1814;
    border-right-color: rgba(255,255,255,0.07);
    --warm-white: #252018;
    --border: rgba(255,255,255,0.09);
    --ink: #f0ebe3;
    --ink-light: #c8bfb4;
    --ink-muted: #7a7068;
    --terracotta-pale: rgba(196,98,45,0.12);
    color: #f0ebe3;
  }
  .desktop-content.dark-mode .sidebar-search-input,
  .desktop-content.dark-mode .sidebar-search-input:focus { background: #1a1714 !important; border-color: rgba(255,255,255,0.1) !important; color: #f0ebe3 !important; }
  .desktop-content.dark-mode .btn-cook-mode { background: #2a2520; color: #f0ebe3; }
  .desktop-content.dark-mode .btn-cook-mode:hover { background: #352e28; }
  .desktop-content.dark-mode .main-content {
    background: #1a1512;
    --warm-white: #1a1512;
    --border: rgba(255,255,255,0.08);
    --ink: #f0ebe3;
    --ink-light: #c8bfb4;
    --ink-muted: #7a7068;
    --terracotta-pale: rgba(196,98,45,0.12);
    color: #f0ebe3;
  }
  .desktop-content.dark-mode .recipe-card:hover { background: rgba(255,255,255,0.05); }
  .desktop-content.dark-mode .recipe-card.active { background: rgba(196,98,45,0.15); }
  .desktop-content.dark-mode .recipe-card-title { color: #f0ebe3; }
  .desktop-content.dark-mode .recipe-card-meta { color: #7a7068; }
  .desktop-content.dark-mode .recipe-thumb-placeholder { background: rgba(255,255,255,0.06); }
  .desktop-content.dark-mode .add-recipe-section { border-bottom-color: rgba(255,255,255,0.07); }
  .desktop-content.dark-mode .recipe-list-header { color: #5a5048; }
  .desktop-content.dark-mode .url-input { background: #231f1a; border-color: rgba(255,255,255,0.1); color: #f0ebe3; }
  .desktop-content.dark-mode .btn-add { background: var(--terracotta); }
  /* Recipe detail dark styles */
  .desktop-content.dark-mode .recipe-detail-title { color: #f0ebe3; }
  .desktop-content.dark-mode .recipe-detail-description { color: #a8a098; }
  .desktop-content.dark-mode .serving-adjuster { background: rgba(255,255,255,0.04); color: #c8bfb4; }
  .desktop-content.dark-mode .serving-adjuster-label { color: #7a7068; }
  .desktop-content.dark-mode .serving-btn { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.15); color: #f0ebe3; }
  .desktop-content.dark-mode .ingredient-item { border-bottom-color: rgba(255,255,255,0.06); color: #e0d8d0; }
  .desktop-content.dark-mode .ingredient-amount { color: var(--terracotta-light); }
  .desktop-content.dark-mode .section-title { color: #f0ebe3; }
  .desktop-content.dark-mode .step-item { border-left-color: rgba(255,255,255,0.1); }
  .desktop-content.dark-mode .step-text { color: #d0c8c0; }
  .desktop-content.dark-mode .btn-toolbar,
  .desktop-content.dark-mode .btn-instagram,
  .desktop-content.dark-mode .btn-delete { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.1); color: #c8bfb4; }
  .desktop-content.dark-mode .btn-toolbar:hover { background: rgba(196,98,45,0.15); border-color: var(--terracotta); color: var(--terracotta-light); }
  .desktop-content.dark-mode .recipe-notes { background: rgba(255,255,255,0.04); color: #c0b8b0; }
  .desktop-content.dark-mode .tag { background: rgba(255,255,255,0.08); color: #a89f94; }
  .desktop-content.dark-mode .recipe-meta-row { border-color: rgba(255,255,255,0.07); }
  .desktop-content.dark-mode .meta-label { color: #5a5048; }
  .desktop-content.dark-mode .meta-value { color: #e0d8d0; }
  .desktop-content.dark-mode .recipe-image-wrapper { background: rgba(255,255,255,0.05); }
  .desktop-content.dark-mode .welcome-screen { color: #7a7068; }
  .desktop-content.dark-mode .empty-collection { color: #7a7068; }
  .desktop-content.dark-mode .empty-collection p { color: #5a5048; }

  /* ── Desktop content area ── */
  .desktop-content {
    flex: 1; display: flex; overflow: hidden;
  }

  /* Layout — recipe list panel + main detail */
  .app-layout {
    display: grid;
    grid-template-columns: 300px 1fr;
    flex: 1; overflow: hidden;
  }

  /* Sidebar (recipe list panel) */
  .sidebar {
    background: var(--warm-white);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  .add-recipe-section {
    padding: 20px 20px 16px;
    border-bottom: 1px solid var(--border);
  }

  .add-recipe-section h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1rem;
    margin-bottom: 14px;
    color: var(--ink);
  }

  /* ── Desktop Home Screen ── */
  .desktop-home {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 48px 60px; gap: 28px;
    background: var(--cream);
    position: relative; overflow: hidden;
  }
  .desktop-home::after {
    content: '';
    position: absolute; top: -80px; right: -80px;
    width: 360px; height: 360px; border-radius: 50%;
    background: radial-gradient(circle, rgba(196,98,45,0.05) 0%, transparent 70%);
    pointer-events: none;
  }
  .home-greeting { text-align: center; }
  .home-eyebrow {
    font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--terracotta); font-weight: 600; margin-bottom: 10px;
  }
  .home-title {
    font-family: 'Playfair Display', serif;
    font-size: 38px; color: var(--ink); line-height: 1.1; margin-bottom: 8px;
  }
  .home-title em { color: var(--terracotta); font-style: italic; }
  .home-subtitle { font-size: 14px; color: var(--ink-muted); }

  .home-import-card {
    width: 100%; max-width: 560px;
  }
  .home-import-label {
    font-size: 10px; letter-spacing: 0.09em; text-transform: uppercase;
    color: var(--ink-muted); font-weight: 600; margin-bottom: 10px;
    display: flex; align-items: center; gap: 6px;
    opacity: 0.6;
  }
  /* Floating pill input bar */
  .home-import-bar {
    display: flex; align-items: center;
    background: var(--warm-white);
    border: 1.5px solid var(--border);
    border-radius: 100px;
    padding: 6px 6px 6px 20px;
    gap: 8px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.06);
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .home-import-bar:focus-within {
    border-color: rgba(196,98,45,0.5);
    box-shadow: 0 2px 20px rgba(196,98,45,0.12);
  }
  .home-import-input {
    flex: 1; border: none; background: transparent;
    padding: 8px 0; font-size: 13.5px; color: var(--ink);
    font-family: 'DM Sans', sans-serif; outline: none;
    min-width: 0;
  }
  .home-import-input::placeholder { color: var(--ink-muted); opacity: 0.6; }
  .home-import-icon-btn {
    width: 38px; height: 38px; border-radius: 50%;
    background: var(--terracotta); border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: white; flex-shrink: 0; transition: opacity 0.15s, transform 0.1s;
    font-size: 17px;
  }
  .home-import-icon-btn:hover { opacity: 0.88; transform: scale(1.05); }
  .home-import-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
  .home-import-icon-btn.loading { animation: spin 0.7s linear infinite; }

  .home-secondary-row {
    display: flex; gap: 8px; margin-top: 14px;
  }
  .home-secondary-btn {
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px;
    padding: 9px 12px; border: 1px solid var(--border); border-radius: 100px;
    background: transparent; cursor: pointer; font-size: 12.5px; font-weight: 500;
    font-family: 'DM Sans', sans-serif; color: var(--ink-muted); transition: all 0.15s;
  }
  .home-secondary-btn:hover { border-color: var(--terracotta); color: var(--terracotta); background: var(--terracotta-pale); }
  .home-secondary-btn i { font-size: 14px; }

  .home-stats { display: flex; gap: 32px; }
  .home-stat { text-align: center; }
  .home-stat-n {
    font-family: 'Playfair Display', serif;
    font-size: 28px; color: var(--ink); line-height: 1;
  }
  .home-stat-l { font-size: 11px; color: var(--ink-muted); margin-top: 3px; }

  .home-hint {
    font-size: 12px; color: var(--ink-muted);
    display: flex; align-items: center; gap: 6px;
  }
  .home-hint svg { width: 13px; height: 13px; }

  .url-input-row {
    display: flex;
    gap: 8px;
  }

  .url-input {
    flex: 1;
    padding: 10px 14px;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.85rem;
    background: white;
    color: var(--ink);
    transition: border-color 0.2s;
    outline: none;
  }
  .url-input:focus { border-color: var(--terracotta); }
  .url-input::placeholder { color: var(--ink-muted); }

  .btn-add {
    background: var(--terracotta);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s, transform 0.1s;
  }
  .btn-add:hover { background: var(--terracotta-light); }
  .btn-add:active { transform: scale(0.97); }
  .btn-add:disabled { opacity: 0.6; cursor: not-allowed; }

  .processing-status {
    margin-top: 12px;
    padding: 10px 14px;
    background: var(--terracotta-pale);
    border-radius: 8px;
    font-size: 0.82rem;
    color: var(--terracotta);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .processing-status.error {
    background: #fdf0f0;
    color: #c44040;
  }
  .processing-status.success {
    background: var(--sage-light);
    color: var(--sage);
  }

  .spinner {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Import progress overlay ── */
  .import-overlay {
    position: fixed; inset: 0; z-index: 2000;
    background: rgba(26,18,9,0.72); backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    padding: 24px;
    animation: fadeInOverlay 0.2s ease;
  }
  @keyframes fadeInOverlay { from { opacity: 0; } to { opacity: 1; } }
  .import-overlay-card {
    background: white; border-radius: 20px;
    padding: 32px 28px; width: 100%; max-width: 360px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.3);
    animation: scaleInCard 0.25s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes scaleInCard { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
  .import-overlay-icon {
    width: 52px; height: 52px; border-radius: 14px;
    background: var(--terracotta-pale); display: flex; align-items: center; justify-content: center;
    font-size: 24px; color: var(--terracotta); margin-bottom: 20px;
  }
  .import-overlay-title {
    font-family: 'Playfair Display', serif; font-size: 1.2rem;
    color: var(--ink); margin-bottom: 6px;
  }
  .import-overlay-subtitle {
    font-size: 13px; color: var(--ink-muted); margin-bottom: 28px; line-height: 1.5;
  }
  .import-steps { display: flex; flex-direction: column; gap: 14px; }
  .import-step { display: flex; align-items: center; gap: 12px; }
  .import-step-dot {
    width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 13px;
    transition: all 0.3s ease;
  }
  .import-step-dot.pending { background: var(--border); color: var(--ink-muted); }
  .import-step-dot.active {
    background: var(--terracotta); color: white;
    box-shadow: 0 0 0 4px rgba(196,98,45,0.2);
    animation: pulseDot 1.2s ease-in-out infinite;
  }
  .import-step-dot.done { background: var(--sage); color: white; }
  @keyframes pulseDot {
    0%, 100% { box-shadow: 0 0 0 4px rgba(196,98,45,0.2); }
    50% { box-shadow: 0 0 0 8px rgba(196,98,45,0.08); }
  }
  .import-step-label { font-size: 13.5px; color: var(--ink-muted); transition: color 0.3s; font-family: 'DM Sans', sans-serif; }
  .import-step-label.active { color: var(--ink); font-weight: 500; }
  .import-step-label.done { color: var(--ink-muted); text-decoration: line-through; }
  .import-overlay-error {
    background: #fdf0f0; border-radius: 10px; padding: 14px 16px;
    color: #c44040; font-size: 13px; line-height: 1.5; margin-top: 20px;
  }
  .import-overlay-error-btn {
    margin-top: 16px; width: 100%; padding: 11px;
    background: var(--terracotta); color: white; border: none;
    border-radius: 10px; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600; cursor: pointer;
  }

  /* Recipe list */
  .recipe-list-header {
    padding: 20px 24px 12px;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-muted);
    font-weight: 500;
  }

  .recipe-list {
    overflow-y: auto;
    flex: 1;
    padding: 0 12px 24px;
  }

  .recipe-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s;
    margin-bottom: 4px;
    position: relative;
  }
  .recipe-card:hover { background: var(--terracotta-pale); }
  .recipe-card.active { background: var(--terracotta-pale); }
  .recipe-card.active::before {
    content: '';
    position: absolute;
    left: 0;
    top: 20%;
    height: 60%;
    width: 3px;
    background: var(--terracotta);
    border-radius: 0 2px 2px 0;
  }

  .recipe-thumb {
    width: 52px;
    height: 52px;
    border-radius: 8px;
    object-fit: cover;
    background: var(--border);
    flex-shrink: 0;
  }
  .recipe-thumb-placeholder {
    width: 52px;
    height: 52px;
    border-radius: 8px;
    background: var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    flex-shrink: 0;
    font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
    will-change: transform;
  }
  /* Emoji preload element — renders off-screen to warm up emoji font */
  .emoji-preloader {
    position: fixed; top: -999px; left: -999px;
    font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
    font-size: 1px; opacity: 0; pointer-events: none; user-select: none;
    white-space: nowrap;
  }

  .recipe-card-info { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; }
  .recipe-card-title {
    font-family: 'Playfair Display', serif;
    font-size: 0.9rem;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .recipe-card-meta {
    font-size: 0.75rem;
    color: var(--ink-muted);
    margin-top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .source-badge {
    font-size: 0.65rem;
    padding: 1px 6px;
    border-radius: 10px;
    background: var(--sage-light);
    color: var(--sage);
  }
  .source-badge.transcribed {
    background: var(--terracotta-pale);
    color: var(--terracotta);
  }

  .empty-collection {
    padding: 32px 24px;
    text-align: center;
    color: var(--ink-muted);
  }
  .empty-collection .big-icon { font-size: 2.5rem; margin-bottom: 12px; }
  .empty-collection p { font-size: 0.85rem; line-height: 1.6; }

  /* Main content */
  .main-content {
    padding: 40px 48px;
    overflow-y: auto;
  }

  .welcome-screen {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 16px;
    color: var(--ink-muted);
  }
  .welcome-screen .big-icon { font-size: 4rem; }
  .welcome-screen h2 {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    color: var(--ink);
  }
  .welcome-screen p { font-size: 0.95rem; max-width: 400px; line-height: 1.7; }

  /* Recipe detail */
  .recipe-detail { max-width: 760px; }

  .recipe-detail-header {
    margin-bottom: 32px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
  }

  .recipe-detail-title {
    font-family: 'Playfair Display', serif;
    font-size: 2.4rem;
    line-height: 1.2;
    color: var(--ink);
    letter-spacing: -0.02em;
  }

  .recipe-detail-description {
    margin-top: 10px;
    font-size: 1rem;
    color: var(--ink-light);
    line-height: 1.7;
  }

  .recipe-image {
    width: 200px;
    height: 200px;
    border-radius: 16px;
    object-fit: cover;
    flex-shrink: 0;
    box-shadow: var(--shadow);
  }

  .recipe-meta-row {
    display: flex;
    gap: 24px;
    padding: 20px 0;
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    margin-bottom: 32px;
    flex-wrap: wrap;
  }

  .meta-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .meta-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-muted);
    font-weight: 500;
  }
  .meta-value {
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--ink);
  }

  .recipe-body { display: grid; grid-template-columns: 260px 1fr; gap: 40px; }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.2rem;
    margin-bottom: 16px;
    color: var(--ink);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Ingredients */
  .ingredient-list { list-style: none; }
  .ingredient-item {
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
    font-size: 0.9rem;
    color: var(--ink-light);
    display: flex;
    gap: 8px;
  }
  .ingredient-amount {
    font-weight: 500;
    color: var(--terracotta);
    min-width: 60px;
    text-align: right;
    flex-shrink: 0;
  }
  .ingredient-notes {
    color: var(--ink-muted);
    font-style: italic;
    font-size: 0.82rem;
  }

  /* Steps */
  .step-list { list-style: none; }
  .step-item {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
  }
  .step-number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--terracotta);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 500;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .step-text { font-size: 0.92rem; color: var(--ink-light); line-height: 1.7; }

  /* Tags */
  .tags-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 28px; }
  .tag {
    padding: 4px 12px;
    background: var(--sage-light);
    color: var(--sage);
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 500;
  }

  /* Notes */
  .recipe-notes {
    margin-top: 28px;
    padding: 16px 20px;
    background: var(--terracotta-pale);
    border-radius: 10px;
    border-left: 3px solid var(--terracotta);
  }
  .recipe-notes h4 {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--terracotta);
    margin-bottom: 6px;
  }
  .recipe-notes p { font-size: 0.88rem; color: var(--ink-light); line-height: 1.6; }

  /* Action buttons */
  .action-bar {
    display: flex;
    gap: 10px;
    margin-bottom: 32px;
  }
  .btn-instagram {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.82rem;
    color: var(--ink-light);
    cursor: pointer;
    text-decoration: none;
    transition: border-color 0.2s;
  }
  .btn-instagram svg { width: 14px; height: 14px; flex-shrink: 0; }
  .btn-instagram:hover { border-color: var(--terracotta); color: var(--terracotta); }

  .btn-delete {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.82rem;
    color: var(--ink-muted);
    cursor: pointer;
    transition: all 0.2s;
    margin-left: auto;
  }
  .btn-delete svg { width: 14px; height: 14px; flex-shrink: 0; }
  .btn-delete:hover { border-color: #e05555; color: #e05555; }

  /* Scrollbar */
  .recipe-list::-webkit-scrollbar { width: 4px; }
  .recipe-list::-webkit-scrollbar-track { background: transparent; }
  .recipe-list::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }

  /* Responsive */

  /* Login screen */
  .login-screen {
    min-height: 100vh;
    background: var(--ink);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 32px;
    text-align: center;
    padding: 24px;
  }
  .login-logo {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    color: var(--cream);
    letter-spacing: -0.03em;
  }
  .login-logo { display: flex; align-items: center; justify-content: center; }
  .login-tagline { color: var(--ink-muted); font-size: 1rem; max-width: 320px; line-height: 1.6; }
  .btn-google {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 28px;
    background: white;
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 1rem;
    font-weight: 500;
    color: var(--ink);
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 4px 16px rgba(0,0,0,0.3);
  }
  .btn-google:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.4); }
  .btn-google svg { width: 20px; height: 20px; }
  .login-loading { color: var(--ink-muted); font-size: 0.9rem; }

  /* User menu in header */
  .header-user {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .user-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--terracotta);
  }
  .user-name { font-size: 0.82rem; color: var(--ink-muted); }
  .btn-logout {
    background: none;
    border: 1px solid rgba(255,255,255,0.15);
    color: var(--ink-muted);
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 0.75rem;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.2s;
  }
  .btn-logout:hover { border-color: var(--terracotta); color: var(--terracotta); }



  .recipe-footer {
    margin-top: 40px;
    padding-top: 24px;
    border-top: 1px solid var(--border);
  }



  /* ── Cook Mode ──────────────────────────────────────────────── */
  .cook-mode-overlay {
    position: fixed; inset: 0; z-index: 1000;
    background: #1a1209;
    display: flex; flex-direction: column;
  }
  .cook-mode-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 28px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    flex-shrink: 0;
  }
  .cook-mode-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem; color: #faf7f2;
    flex: 1; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    margin-right: 16px;
  }
  .cook-mode-exit {
    background: rgba(255,255,255,0.1); border: none; border-radius: 8px;
    color: #9a8f80; font-size: 0.8rem; padding: 6px 14px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; flex-shrink: 0;
    transition: background 0.15s;
  }
  .cook-mode-exit:hover { background: rgba(255,255,255,0.18); }
  .cook-mode-progress {
    height: 3px; background: rgba(255,255,255,0.08);
    flex-shrink: 0;
  }
  .cook-mode-progress-fill {
    height: 100%; background: var(--terracotta);
    transition: width 0.4s ease;
  }
  .cook-mode-body {
    flex: 1; display: flex; flex-direction: column;
    justify-content: center; align-items: center;
    padding: 40px 40px 20px; overflow: hidden;
  }
  .cook-mode-step-label {
    font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--terracotta); font-family: 'DM Sans', sans-serif;
    font-weight: 600; margin-bottom: 20px;
  }
  .cook-mode-step-text {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.3rem, 3.5vw, 2rem);
    color: #faf7f2; line-height: 1.6; text-align: center;
    max-width: 680px;
  }
  .cook-mode-nav {
    display: flex; align-items: center; justify-content: center;
    gap: 24px; padding: 24px 40px 36px; flex-shrink: 0;
  }
  .cook-mode-nav-btn {
    width: 56px; height: 56px; border-radius: 50%;
    border: 1.5px solid rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.06);
    color: #faf7f2; font-size: 22px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
  }
  .cook-mode-nav-btn:hover:not(:disabled) { background: rgba(255,255,255,0.14); border-color: rgba(255,255,255,0.4); }
  .cook-mode-nav-btn:disabled { opacity: 0.25; cursor: default; }
  .cook-mode-counter {
    font-family: 'DM Sans', sans-serif;
    font-size: 0.85rem; color: #9a8f80; min-width: 60px; text-align: center;
  }
  .cook-mode-ingredients {
    background: rgba(255,255,255,0.04);
    border-radius: 12px; padding: 16px 20px; margin-top: 24px;
    max-width: 680px; width: 100%;
    border: 1px solid rgba(255,255,255,0.06);
  }
  .cook-mode-ing-label {
    font-size: 0.68rem; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--terracotta); font-family: 'DM Sans', sans-serif; font-weight: 600;
    margin-bottom: 10px;
  }
  .cook-mode-ing-row {
    display: flex; gap: 12px; align-items: baseline;
    font-family: 'DM Sans', sans-serif; font-size: 0.88rem;
    color: rgba(250,247,242,0.75); padding: 4px 0;
  }
  .cook-mode-ing-amount { color: var(--terracotta); font-weight: 600; min-width: 60px; text-align: right; }
  .cook-mode-done-screen {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 16px; padding: 40px;
  }
  .cook-mode-done-emoji { font-size: 72px; animation: pop 0.5s ease; }
  @keyframes pop { 0% { transform: scale(0); } 70% { transform: scale(1.2); } 100% { transform: scale(1); } }
  .cook-mode-done-title { font-family: 'Playfair Display', serif; font-size: 2rem; color: #faf7f2; }
  .cook-mode-done-sub { color: #9a8f80; font-size: 0.9rem; font-family: 'DM Sans', sans-serif; }

  /* ── Shopping List ───────────────────────────────────────────── */
  .shopping-panel {
    position: fixed; inset: 0; z-index: 999;
    display: flex; align-items: stretch; justify-content: flex-end;
  }
  .shopping-backdrop {
    position: absolute; inset: 0;
    background: rgba(26,18,9,0.5); backdrop-filter: blur(2px);
  }
  .shopping-drawer {
    position: relative; z-index: 1;
    width: 440px; max-width: 100vw;
    background: #faf7f2; display: flex; flex-direction: column;
    box-shadow: -8px 0 40px rgba(0,0,0,0.15);
    animation: slideInRight 0.3s ease;
  }
  .shopping-header {
    padding: 24px 24px 16px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 12px; flex-shrink: 0;
  }
  .shopping-title { font-family: 'Playfair Display', serif; font-size: 1.4rem; color: var(--ink); flex: 1; }
  .shopping-close { background: none; border: none; font-size: 20px; cursor: pointer; color: var(--ink-muted); padding: 4px; }
  .shopping-body { flex: 1; overflow-y: auto; padding: 16px 24px; }
  .shopping-recipe-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--terracotta-pale); border: 1px solid var(--border);
    border-radius: 20px; padding: 4px 12px; margin: 4px;
    font-size: 0.8rem; font-family: 'DM Sans', sans-serif; color: var(--ink);
    cursor: pointer; transition: all 0.15s;
  }
  .shopping-recipe-chip.selected { background: var(--terracotta); color: white; border-color: var(--terracotta); }
  .shopping-recipe-chip.selected .chip-remove { color: rgba(255,255,255,0.7); }
  .shopping-section-label {
    font-size: 0.68rem; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--terracotta); font-family: 'DM Sans', sans-serif; font-weight: 600;
    margin: 20px 0 10px;
  }
  .shopping-item {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 8px 0; border-bottom: 1px solid rgba(196,183,168,0.3);
    cursor: pointer; transition: opacity 0.2s;
  }
  .shopping-item.checked { opacity: 0.4; }
  .shopping-item.checked .shopping-item-name { text-decoration: line-through; }
  .shopping-check {
    width: 20px; height: 20px; border-radius: 5px; flex-shrink: 0; margin-top: 1px;
    border: 1.5px solid rgba(196,183,168,0.6); display: flex; align-items: center; justify-content: center;
    transition: all 0.15s; font-size: 12px; color: white;
  }
  .shopping-item.checked .shopping-check { background: var(--terracotta); border-color: var(--terracotta); }

  /* ── Dark mode: home screen ── */
  .desktop-home.dark-mode { background: #1a1512; }
  .desktop-home.dark-mode .home-eyebrow { color: var(--terracotta-light); }
  .desktop-home.dark-mode .home-title { color: #f0ebe3; }
  .desktop-home.dark-mode .home-subtitle { color: #7a7068; }
  .desktop-home.dark-mode .home-import-bar {
    background: rgba(255,255,255,0.05);
    border-color: rgba(255,255,255,0.1);
    box-shadow: none;
  }
  .desktop-home.dark-mode .home-import-bar:focus-within {
    border-color: rgba(196,98,45,0.4);
    box-shadow: 0 2px 20px rgba(196,98,45,0.08);
  }
  .desktop-home.dark-mode .home-import-input { color: #f0ebe3; }
  .desktop-home.dark-mode .home-import-input::placeholder { color: rgba(255,255,255,0.25); }
  .desktop-home.dark-mode .home-secondary-btn { border-color: rgba(255,255,255,0.1); color: rgba(255,255,255,0.35); }
  .desktop-home.dark-mode .home-secondary-btn:hover { border-color: var(--terracotta); color: var(--terracotta-light); background: rgba(196,98,45,0.1); }

  /* ── Dark mode: shopping list ── */
  .shopping-drawer.dark-mode { background: #1c1814; }
  .shopping-drawer.dark-mode .shopping-header { border-bottom-color: rgba(255,255,255,0.07); }
  .shopping-drawer.dark-mode .shopping-title { color: #f0ebe3; }
  .shopping-drawer.dark-mode .shopping-close { color: #7a7068; }
  .shopping-drawer.dark-mode .shopping-item { border-bottom-color: rgba(255,255,255,0.06); }
  .shopping-drawer.dark-mode .shopping-item-name { color: #e0d8d0; }
  .shopping-drawer.dark-mode .shopping-check { border-color: rgba(255,255,255,0.2); }
  .shopping-drawer.dark-mode .shopping-footer { border-top-color: rgba(255,255,255,0.07); }
  .shopping-drawer.dark-mode .shopping-btn-clear { background: #251f1a; border-color: rgba(255,255,255,0.1); color: #9a9088; }
  .shopping-drawer.dark-mode .shopping-recipe-chip { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.1); color: #c8bfb4; }
  .shopping-drawer.dark-mode .shopping-section-label { color: var(--terracotta-light); }

  /* ── Dark mode: AI panels ── */
  .ai-panel.dark-mode .ai-drawer { background: #141210; }
  .ai-panel:not(.dark-mode) .ai-drawer {
    background: #faf7f2;
    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
  }
  .ai-panel:not(.dark-mode) .ai-header { border-bottom-color: rgba(0,0,0,0.08); }
  .ai-panel:not(.dark-mode) .ai-header-title { color: var(--ink); }
  .ai-panel:not(.dark-mode) .ai-header-sub { color: var(--ink-muted); }
  .ai-panel:not(.dark-mode) .ai-close { color: var(--ink-muted); }
  .ai-panel:not(.dark-mode) .ai-msg.assistant {
    background: rgba(0,0,0,0.04); color: var(--ink-light);
    border-color: rgba(0,0,0,0.07);
  }
  .ai-panel:not(.dark-mode) .ai-msg.thinking { background: rgba(0,0,0,0.03); color: var(--ink-muted); }
  .ai-panel:not(.dark-mode) .ai-input-row { border-top-color: rgba(0,0,0,0.08); background: white; }
  .ai-panel:not(.dark-mode) .ai-input { background: #f4f0eb; border-color: rgba(0,0,0,0.1); color: var(--ink); }
  .ai-panel:not(.dark-mode) .ai-empty-state { color: var(--ink-light); }
  .ai-panel:not(.dark-mode) .ai-empty-icon { color: var(--terracotta); }
  .shopping-item-amount { font-size: 0.82rem; color: var(--terracotta); font-weight: 600; min-width: 70px; font-family: 'DM Sans', sans-serif; }
  .shopping-item-name { font-size: 0.88rem; color: var(--ink); font-family: 'DM Sans', sans-serif; flex: 1; }
  .shopping-footer {
    padding: 16px 24px; border-top: 1px solid var(--border);
    display: flex; gap: 10px; flex-shrink: 0;
  }
  .shopping-btn-share {
    flex: 1; padding: 10px; background: var(--terracotta); color: white;
    border: none; border-radius: 10px; font-family: 'DM Sans', sans-serif;
    font-size: 0.88rem; font-weight: 600; cursor: pointer; transition: opacity 0.15s;
  }
  .shopping-btn-share:hover { opacity: 0.88; }
  .shopping-btn-clear {
    padding: 10px 16px; background: white; color: var(--ink-muted);
    border: 1.5px solid var(--border); border-radius: 10px;
    font-family: 'DM Sans', sans-serif; font-size: 0.88rem; cursor: pointer;
  }

  /* ── AI Chat Panels ──────────────────────────────────────────── */
  .ai-panel {
    position: fixed; inset: 0; z-index: 999;
    display: flex; align-items: stretch; justify-content: flex-end;
  }
  .ai-backdrop { position: absolute; inset: 0; background: rgba(26,18,9,0.5); backdrop-filter: blur(2px); }
  .ai-drawer {
    position: relative; z-index: 1;
    width: 480px; max-width: 100vw;
    background: #1a1209; display: flex; flex-direction: column;
    box-shadow: -8px 0 40px rgba(0,0,0,0.3);
    animation: slideInRight 0.3s ease;
  }
  .ai-header {
    padding: 22px 24px 18px; border-bottom: 1px solid rgba(255,255,255,0.08);
    display: flex; align-items: center; gap: 12px; flex-shrink: 0;
  }
  .ai-header-icon { font-size: 24px; }
  .ai-header-text { flex: 1; }
  .ai-header-title { font-family: 'Playfair Display', serif; font-size: 1.1rem; color: #faf7f2; }
  .ai-header-sub { font-size: 0.75rem; color: #9a8f80; font-family: 'DM Sans', sans-serif; margin-top: 1px; }
  .ai-close { background: none; border: none; font-size: 20px; cursor: pointer; color: #9a8f80; padding: 4px; }
  .ai-body { flex: 1; overflow-y: auto; padding: 20px 24px; display: flex; flex-direction: column; gap: 14px; }
  .ai-msg {
    max-width: 88%; padding: 12px 16px; border-radius: 16px;
    font-family: 'DM Sans', sans-serif; font-size: 0.88rem; line-height: 1.6;
    animation: fadeUp 0.25s ease;
  }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .ai-msg.user { background: var(--terracotta); color: white; align-self: flex-end; border-bottom-right-radius: 4px; }
  .ai-msg.assistant {
    background: rgba(255,255,255,0.07); color: #e8e0d4;
    align-self: flex-start; border-bottom-left-radius: 4px;
    border: 1px solid rgba(255,255,255,0.06);
  }
  .ai-msg.thinking {
    background: rgba(255,255,255,0.04); color: #9a8f80;
    align-self: flex-start; border-bottom-left-radius: 4px;
    font-style: italic; display: flex; align-items: center; gap: 8px;
  }
  .ai-typing-dots span {
    display: inline-block; width: 5px; height: 5px; border-radius: 50%;
    background: #9a8f80; margin: 0 1px;
    animation: blink 1.2s ease infinite;
  }
  .ai-typing-dots span:nth-child(2) { animation-delay: 0.2s; }
  .ai-typing-dots span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes blink { 0%,80%,100% { opacity: 0.2; } 40% { opacity: 1; } }
  .ai-footer { padding: 16px 24px; border-top: 1px solid rgba(255,255,255,0.08); flex-shrink: 0; }
  .ai-input-row { display: flex; gap: 10px; }
  .ai-input {
    flex: 1; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px; padding: 10px 14px; color: #faf7f2;
    font-family: 'DM Sans', sans-serif; font-size: 16px; outline: none;
    transition: border-color 0.15s;
  }
  .ai-input::placeholder { color: #9a8f80; }
  .ai-input:focus { border-color: var(--terracotta); }
  .ai-send {
    width: 42px; height: 42px; border-radius: 10px;
    background: var(--terracotta); border: none; color: white;
    font-size: 16px; cursor: pointer; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    transition: opacity 0.15s;
  }
  .ai-send:hover { opacity: 0.85; }
  .ai-send:disabled { opacity: 0.4; cursor: default; }
  .ai-suggestion-chips {
    display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px;
  }
  .ai-chip {
    background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px; padding: 6px 14px;
    font-size: 0.78rem; color: #c8bfb0; font-family: 'DM Sans', sans-serif;
    cursor: pointer; transition: all 0.15s; white-space: nowrap;
  }
  .ai-chip:hover { background: rgba(196,98,45,0.2); border-color: var(--terracotta); color: #faf7f2; }
  
  /* What Can I Make - ingredient input */
  .wcim-ingredients {
    display: flex; flex-wrap: wrap; gap: 8px; 
    padding: 12px 14px; 
    background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.15);
    border-radius: 10px; min-height: 48px; cursor: text;
    transition: border-color 0.15s;
  }
  /* Light mode override for WCIM */
  .ai-panel:not(.dark-mode) .wcim-ingredients {
    background: var(--cream); border-color: var(--border);
  }
  .wcim-ingredients:focus-within { border-color: var(--terracotta); }
  .wcim-tag {
    background: var(--terracotta); color: white; border-radius: 20px;
    padding: 3px 10px 3px 12px; font-size: 0.8rem; display: flex; align-items: center; gap: 6px;
    font-family: 'DM Sans', sans-serif; animation: fadeUp 0.15s ease;
  }
  .wcim-tag-remove { cursor: pointer; opacity: 0.7; font-size: 13px; line-height: 1; }
  .wcim-tag-remove:hover { opacity: 1; }
  .wcim-input {
    background: none; border: none; outline: none; color: #faf7f2;
    font-family: 'DM Sans', sans-serif; font-size: 0.88rem;
    min-width: 120px; flex: 1; padding: 2px 0;
  }
  .wcim-input::placeholder { color: rgba(255,255,255,0.4); }
  .ai-panel:not(.dark-mode) .wcim-input { color: var(--ink); }
  .ai-panel:not(.dark-mode) .wcim-input::placeholder { color: var(--ink-muted); }

  .btn-cook-mode {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 20px; background: var(--ink); color: #faf7f2;
    border: none; border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
    transition: all 0.15s; white-space: nowrap;
  }
  .btn-cook-mode svg { width: 16px; height: 16px; flex-shrink: 0; }
  .btn-cook-mode:hover { background: #2d1f0e; transform: translateY(-1px); }

  /* Shopping list + AI buttons in toolbar */
  .recipe-toolbar {
    display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 4px;
  }
  .btn-toolbar {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 14px; background: white;
    border: 1.5px solid var(--border); border-radius: 8px;
    cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 0.8rem;
    color: var(--ink); transition: all 0.15s; font-weight: 500;
  }
  .btn-toolbar svg { width: 14px; height: 14px; flex-shrink: 0; }
  .btn-toolbar:hover { border-color: var(--terracotta); color: var(--terracotta); background: var(--terracotta-pale); }

  /* ── Skeleton loader ─────────────────────────── */
  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  .skel {
    background: linear-gradient(90deg,
      var(--border) 25%,
      #ede8e0 50%,
      var(--border) 75%
    );
    background-size: 800px 100%;
    animation: shimmer 1.4s ease-in-out infinite;
    border-radius: 6px;
  }
  .skel-circle { border-radius: 50% !important; }
  .skel-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 24px;
    border-bottom: 1px solid var(--border);
  }
  .skel-detail-hero {
    background: linear-gradient(90deg, var(--border) 25%, #ede8e0 50%, var(--border) 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s ease-in-out infinite;
    border-radius: 12px;
    width: 180px;
    height: 180px;
    flex-shrink: 0;
  }

  /* Creator strip */
  .creator-strip {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 0;
    margin-bottom: 8px;
    border-bottom: 1px solid var(--border);
  }
  .creator-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--terracotta);
    flex-shrink: 0;
  }
  .creator-avatar-placeholder {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: var(--terracotta-pale);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
    border: 2px solid var(--border);
  }
  .creator-name {
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--ink);
  }
  .creator-handle {
    font-size: 0.78rem;
    color: var(--ink-muted);
    margin-top: 1px;
  }
  .creator-follow {
    margin-left: auto;
    font-size: 0.78rem;
    color: var(--terracotta);
    text-decoration: none;
    border: 1px solid var(--terracotta);
    border-radius: 6px;
    padding: 4px 10px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .creator-follow:hover { background: var(--terracotta-pale); }

  /* Serving adjuster */
  .serving-adjuster {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: var(--terracotta-pale);
    border-radius: 10px;
    margin: 16px 0 8px;
  }
  .serving-adjuster-label {
    font-size: 0.82rem;
    color: var(--ink-muted);
    font-weight: 500;
  }
  .serving-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
  }
  .serving-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1.5px solid var(--terracotta);
    background: white;
    color: var(--terracotta);
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-weight: 300;
    transition: all 0.15s;
  }
  .serving-btn:hover { background: var(--terracotta); color: white; }
  .serving-count {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--ink);
    min-width: 24px;
    text-align: center;
  }
  .serving-reset {
    font-size: 0.75rem;
    color: var(--ink-muted);
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
  }

  /* Desktop creator list */
  .creator-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 24px;
    cursor: pointer;
    border-bottom: 1px solid var(--border);
    transition: background 0.15s;
  }
  .creator-card:hover { background: var(--terracotta-pale); }
  .creator-card-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--terracotta);
    flex-shrink: 0;
  }
  .creator-card-avatar-placeholder {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: var(--terracotta-pale);
    border: 2px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }
  .creator-card-info { flex: 1; min-width: 0; }
  .creator-card-name { font-size: 0.88rem; font-weight: 600; color: var(--ink); }
  .creator-card-count { font-size: 0.75rem; color: var(--ink-muted); margin-top: 2px; }
  .creator-recipe-indent {
    padding-left: 20px;
    border-left: 2px solid var(--terracotta-pale);
    margin-left: 24px;
  }

  /* Mobile creator tab */
  .mobile-creator-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 20px;
    border-bottom: 1px solid var(--border);
    cursor: pointer;
  }
  .mobile-creator-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--terracotta);
    flex-shrink: 0;
  }
  .mobile-creator-avatar-placeholder {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: var(--terracotta-pale);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    flex-shrink: 0;
  }
  .mobile-creator-name { font-family: 'Playfair Display', serif; font-size: 15px; color: var(--ink); font-weight: 600; }
  .mobile-creator-count { font-size: 12px; color: var(--ink-muted); margin-top: 2px; }

  /* Mobile serving adjuster */
  .mobile-serving-adjuster {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 20px;
    background: var(--terracotta-pale);
    border-radius: 10px;
    margin: 12px 0;
  }

  /* Photo upload */
  .photo-upload-area {
    width: 200px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .recipe-image-wrapper {
    position: relative;
    width: 200px;
    height: 200px;
    border-radius: 16px;
    overflow: hidden;
    background: var(--border);
    flex-shrink: 0;
  }
  .recipe-image-wrapper:hover .photo-overlay { opacity: 1; }
  .recipe-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    box-shadow: var(--shadow);
    display: block;
  }
  .photo-overlay {
    position: absolute;
    inset: 0;
    background: rgba(26,18,9,0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    cursor: pointer;
    border-radius: 16px;
  }
  .photo-overlay span { color: white; font-size: 0.82rem; font-weight: 500; }
  .btn-upload-photo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    background: white;
    border: 1.5px dashed var(--border);
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.8rem;
    color: var(--ink-muted);
    cursor: pointer;
    transition: all 0.2s;
    width: 200px;
    height: 200px;
    flex-direction: column;
    gap: 8px;
  }
  .btn-upload-photo:hover { border-color: var(--terracotta); color: var(--terracotta); background: var(--terracotta-pale); }
  .btn-upload-photo .upload-icon { font-size: 2rem; }
  .btn-upload-photo-small {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 12px;
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.78rem;
    color: var(--ink-muted);
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;
  }
  .btn-upload-photo-small:hover { border-color: var(--terracotta); color: var(--terracotta); }
  .processing-photo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.8rem;
    color: var(--terracotta);
    padding: 8px;
  }



  /* ── Panel animations (must be outside media queries) ── */
  @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
  @keyframes slideInUp { from { transform: translateY(100%); } to { transform: translateY(0); } }

  /* ── Mobile panel overrides ── */
  @media (max-width: 768px) {
    /* AI panel: full screen so keyboard works naturally */
    .ai-panel { align-items: stretch; justify-content: stretch; }
    .ai-panel .ai-backdrop { display: none; }
    .ai-panel .ai-drawer {
      width: 100%; max-width: 100%; max-height: 100%;
      border-radius: 0; box-shadow: none;
      animation: none;
    }
    .ai-body { flex: 1; min-height: 0; overflow-y: auto; -webkit-overflow-scrolling: touch; }
    .ai-footer { flex-shrink: 0; padding-bottom: env(safe-area-inset-bottom, 16px); }
    .ai-suggestion-chips { flex-shrink: 0; padding: 0 24px 8px; }

    /* Shopping: slides up from bottom, no drag handle */
    .shopping-panel { align-items: flex-end; justify-content: stretch; }
    .shopping-drawer {
      width: 100%; max-height: 92vh;
      border-radius: 20px 20px 0 0;
      animation: slideInUp 0.3s ease;
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }
    /* AI panel mobile: match app colors */
    .ai-panel:not(.dark-mode) .ai-footer { background: white !important; }
    .ai-panel:not(.dark-mode) .ai-footer .wcim-ingredients { background: #f4f0eb; border-color: rgba(0,0,0,0.1); }
    .ai-panel:not(.dark-mode) .wcim-input { color: var(--ink); }
    .ai-panel:not(.dark-mode) .wcim-tag { background: var(--terracotta); color: white; }
  }


  /* ── Desktop account popover ── */
  .account-popover-wrap { position: relative; }
  .account-popover {
    position: fixed;
    width: 272px; background: #1e1510; border: 1px solid rgba(255,255,255,0.1);
    border-radius: 14px; overflow: hidden;
    box-shadow: 0 16px 48px rgba(0,0,0,0.6);
    animation: fadeUp 0.18s ease;
    z-index: 500;
  }
  .account-popover-header {
    display: flex; align-items: center; gap: 12px;
    padding: 16px; border-bottom: 1px solid rgba(255,255,255,0.07);
  }
  .account-popover-avatar { width: 38px; height: 38px; border-radius: 50%; border: 2px solid rgba(196,98,45,0.6); object-fit: cover; flex-shrink: 0; }
  .account-popover-avatar-placeholder { width: 38px; height: 38px; border-radius: 50%; background: var(--terracotta); display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; color: white; flex-shrink: 0; font-family: 'DM Sans', sans-serif; }
  .account-popover-name { font-size: 13px; color: rgba(255,255,255,0.85); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .account-popover-email { font-size: 11px; color: rgba(255,255,255,0.35); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .account-popover-item {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 16px; width: 100%; background: none; border: none;
    cursor: pointer; text-align: left; font-family: 'DM Sans', sans-serif;
    transition: background 0.12s;
  }
  .account-popover-item:hover { background: rgba(255,255,255,0.05); }
  .account-popover-item-icon { font-size: 17px; flex-shrink: 0; width: 20px; text-align: center; color: rgba(255,255,255,0.5); }
  .account-popover-item-label { font-size: 13px; color: rgba(255,255,255,0.7); }
  .account-popover-item.danger .account-popover-item-label { color: #e06060; }
  .account-popover-item.danger .account-popover-item-icon { color: #e06060; }

  /* Nav footer user button */
  .nav-footer-user-btn {
    display: flex; align-items: center; gap: 10px;
    padding: 7px 10px; width: 100%; background: none; border: none;
    cursor: pointer; border-radius: 8px; text-align: left;
    transition: background 0.15s; -webkit-tap-highlight-color: transparent;
  }
  .nav-footer-user-btn:hover { background: rgba(255,255,255,0.06); }
  .nav-footer-settings-icon { font-size: 14px; color: rgba(255,255,255,0.25); margin-left: auto; flex-shrink: 0; transition: color 0.15s; }
  .nav-footer-user-btn:hover .nav-footer-settings-icon { color: rgba(255,255,255,0.55); }


  /* ── Recipe edit banner ── */
  .edit-nudge {
    display: flex; align-items: center; justify-content: space-between;
    background: #fdf6f0; border: 1.5px solid #f0d5c0; border-radius: 12px;
    padding: 11px 14px; margin-bottom: 24px; gap: 12px;
    animation: fadeIn 0.3s ease;
  }
  .edit-nudge-text { font-size: 13px; color: var(--ink-light); flex: 1; }
  .edit-nudge-text strong { color: var(--ink); font-weight: 600; }
  .edit-nudge-btn {
    background: var(--terracotta); color: white; border: none;
    border-radius: 8px; padding: 6px 14px; font-size: 13px; font-weight: 500;
    font-family: 'DM Sans', sans-serif; cursor: pointer; flex-shrink: 0;
    transition: background 0.15s;
  }
  .edit-nudge-btn:hover { background: #b0561f; }
  .edit-nudge-dismiss { background: none; border: none; cursor: pointer; color: var(--ink-muted); font-size: 18px; line-height: 1; padding: 0 0 0 4px; }

  /* Edit mode button in toolbar */
  .btn-edit {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: 8px; border: 1.5px solid var(--border);
    background: white; font-family: 'DM Sans', sans-serif; font-size: 13px;
    color: var(--ink-light); cursor: pointer; transition: all 0.15s;
    font-weight: 500;
  }
  .btn-edit:hover { border-color: var(--terracotta); color: var(--terracotta); background: var(--terracotta-pale); }
  .btn-edit.active { background: var(--terracotta); color: white; border-color: var(--terracotta); }

  /* Edit mode overlay feel */
  .recipe-detail.editing .recipe-detail-title { display: none; }
  .recipe-detail.editing .recipe-detail-description { display: none; }

  /* Edit form */
  .edit-form { display: flex; flex-direction: column; gap: 0; }
  .edit-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 18px; }
  .edit-label {
    font-size: 11px; text-transform: uppercase; letter-spacing: 0.07em;
    color: var(--ink-muted); font-weight: 600;
  }
  .edit-input {
    font-family: 'DM Sans', sans-serif; font-size: 15px; color: var(--ink);
    background: white; border: 1.5px solid var(--border); border-radius: 9px;
    padding: 9px 12px; outline: none; transition: border-color 0.15s;
    width: 100%;
  }
  .edit-input:focus { border-color: var(--terracotta); }
  .edit-title-input {
    font-family: 'Playfair Display', serif; font-size: 1.5rem; color: var(--ink);
    background: white; border: 1.5px solid var(--border); border-radius: 9px;
    padding: 10px 14px; outline: none; transition: border-color 0.15s;
    width: 100%;
  }
  .edit-title-input:focus { border-color: var(--terracotta); }
  .edit-meta-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 18px;
  }
  .edit-textarea {
    font-family: 'DM Sans', sans-serif; font-size: 14px; color: var(--ink);
    background: white; border: 1.5px solid var(--border); border-radius: 9px;
    padding: 9px 12px; outline: none; transition: border-color 0.15s;
    width: 100%; resize: vertical; min-height: 72px; line-height: 1.5;
  }
  .edit-textarea:focus { border-color: var(--terracotta); }

  /* Ingredient edit rows */
  .edit-ingredients { display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px; }
  .edit-ingredient-row {
    display: grid; grid-template-columns: 70px 70px 1fr auto; gap: 6px; align-items: center;
  }
  .edit-ingredient-row .edit-input { font-size: 13px; padding: 7px 9px; }
  .edit-row-remove {
    background: none; border: none; cursor: pointer; color: var(--ink-muted);
    font-size: 18px; line-height: 1; padding: 4px; border-radius: 6px;
    transition: color 0.12s, background 0.12s;
  }
  .edit-row-remove:hover { color: #c44040; background: #fdf0f0; }
  .edit-add-btn {
    display: flex; align-items: center; gap: 6px; background: none;
    border: 1.5px dashed var(--border); border-radius: 8px; padding: 7px 12px;
    font-family: 'DM Sans', sans-serif; font-size: 13px; color: var(--ink-muted);
    cursor: pointer; transition: all 0.15s; width: 100%; margin-top: 2px;
  }
  .edit-add-btn:hover { border-color: var(--terracotta); color: var(--terracotta); }

  /* Step edit rows */
  .edit-steps { display: flex; flex-direction: column; gap: 8px; margin-bottom: 8px; }
  .edit-step-row { display: grid; grid-template-columns: auto 1fr auto; gap: 8px; align-items: flex-start; }
  .edit-step-num {
    width: 26px; height: 26px; border-radius: 50%; background: var(--terracotta-pale);
    color: var(--terracotta); font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 8px;
  }

  /* Edit action bar */
  .edit-actions {
    display: flex; gap: 10px; padding: 16px 0 8px;
    border-top: 1px solid var(--border); margin-top: 8px;
  }
  .btn-save {
    background: var(--terracotta); color: white; border: none;
    border-radius: 10px; padding: 10px 24px; font-size: 14px; font-weight: 600;
    font-family: 'DM Sans', sans-serif; cursor: pointer; transition: background 0.15s;
  }
  .btn-save:hover { background: #b0561f; }
  .btn-save:disabled { opacity: 0.6; cursor: default; }
  .btn-cancel-edit {
    background: white; color: var(--ink-light); border: 1.5px solid var(--border);
    border-radius: 10px; padding: 10px 20px; font-size: 14px; font-weight: 500;
    font-family: 'DM Sans', sans-serif; cursor: pointer; transition: all 0.15s;
  }
  .btn-cancel-edit:hover { border-color: var(--ink-muted); color: var(--ink); }

  .edit-section-header {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;
  }

  /* ── Mobile bottom nav layout ── */
  @media (max-width: 768px) {
    .header { display: none !important; }
    .app-layout { display: none !important; }
    .sidebar { display: none !important; }
    .main-content { display: none !important; }

    /* ── Mobile shell ── */
    .mobile-shell { 
      position: fixed; inset: 0;
      display: flex; flex-direction: column;
      background: var(--cream);
      overflow: hidden;
    }
    .mobile-header {
      background: var(--ink); padding: 14px 20px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: space-between;
    }
    .mobile-header-logo { display: flex; align-items: center; gap: 8px; }
    .mobile-content { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; min-height: 0; }

    /* ── Bottom nav: 5 items, consistent flat style ── */
    .mobile-bottom-nav {
      display: flex; border-top: 1px solid var(--border);
      background: white; flex-shrink: 0;
      padding-bottom: env(safe-area-inset-bottom, 4px);
    }
    .mobile-tab {
      flex: 1; padding: 10px 0 7px; border: none; background: transparent;
      cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 3px;
      -webkit-tap-highlight-color: transparent;
    }
    .mobile-tab-icon { font-size: 22px; color: var(--ink-muted); line-height: 1; }
    .mobile-tab-label { font-size: 10px; color: var(--ink-muted); font-family: 'DM Sans', sans-serif; font-weight: 400; line-height: 1; }
    .mobile-tab.active .mobile-tab-icon { color: var(--terracotta); }
    .mobile-tab.active .mobile-tab-label { color: var(--terracotta); font-weight: 600; }
    /* Add tab: terracotta circle, no label */
    .mobile-tab.add-tab .mobile-tab-icon {
      width: 40px; height: 40px; border-radius: 50%;
      background: var(--terracotta); color: white;
      display: flex; align-items: center; justify-content: center;
      font-size: 22px; margin-top: -8px;
      box-shadow: 0 3px 10px rgba(196,98,45,0.4);
    }

    /* ── Mobile Home ── */
    .mobile-home { padding: 32px 20px 28px; display: flex; flex-direction: column; gap: 28px; }
    .mobile-home-eyebrow { font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--terracotta); font-weight: 600; margin-bottom: 4px; }
    .mobile-home-title { font-family: 'Playfair Display', serif; font-size: 30px; color: var(--ink); line-height: 1.15; }
    .mobile-home-title em { color: var(--terracotta); font-style: italic; }
    .mobile-home-sub { font-size: 13px; color: var(--ink-muted); line-height: 1.55; }

    /* Import pill — matches desktop */
    .mobile-import-bar {
      display: flex; align-items: center;
      background: white; border: 1.5px solid var(--border);
      border-radius: 100px; padding: 5px 5px 5px 18px; gap: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.05);
    }
    .mobile-import-bar:focus-within { border-color: rgba(196,98,45,0.45); box-shadow: 0 2px 14px rgba(196,98,45,0.1); }
    .mobile-import-url {
      flex: 1; border: none; background: transparent; outline: none;
      font-size: 15px; color: var(--ink); font-family: 'DM Sans', sans-serif; padding: 8px 0;
    }
    .mobile-import-url::placeholder { color: var(--ink-muted); opacity: 0.55; }
    .mobile-import-icon-btn {
      width: 38px; height: 38px; border-radius: 50%;
      background: var(--terracotta); border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      color: white; flex-shrink: 0; font-size: 17px; transition: opacity 0.15s;
    }
    .mobile-import-icon-btn:disabled { opacity: 0.35; }

    /* Recently saved */
    .mobile-recent-label { font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-muted); font-weight: 600; }
    .mobile-recent-list { display: flex; flex-direction: column; }
    .mobile-recent-row {
      display: flex; align-items: center; gap: 13px;
      padding: 12px 0; border-bottom: 1px solid var(--border); cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }
    .mobile-recent-row:last-child { border-bottom: none; }
    .mobile-recent-thumb {
      width: 42px; height: 42px; border-radius: 9px;
      background: var(--terracotta-pale); flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      font-size: 22px; line-height: 1;
      font-family: "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif;
      will-change: transform;
    }
    .mobile-recent-thumb.has-image { overflow: hidden; }
    .mobile-recent-thumb.has-image img { width: 100%; height: 100%; object-fit: cover; border-radius: 9px; }
    .mobile-recent-info { flex: 1; min-width: 0; }
    .mobile-recent-title { font-size: 13.5px; font-weight: 500; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .mobile-recent-meta { font-size: 11px; color: var(--ink-muted); margin-top: 2px; }
    .mobile-recent-arrow { color: var(--border); font-size: 16px; }

    .mobile-home-hint { font-size: 11.5px; color: var(--ink-muted); display: flex; align-items: center; gap: 5px; }

    /* Mobile recipe list */
    .mobile-recipe-list { padding: 0; }
    .mobile-recipe-item {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 14px 20px;
      border-bottom: 1px solid var(--border);
      cursor: pointer;
      background: white;
      transition: background 0.15s;
    }
    .mobile-recipe-item:active { background: var(--terracotta-pale); }
    .mobile-recipe-thumb {
      width: 56px;
      height: 56px;
      border-radius: 12px;
      object-fit: cover;
      flex-shrink: 0;
    }
    .mobile-recipe-emoji { font-family: "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif;
      width: 56px;
      height: 56px;
      border-radius: 12px;
      background: var(--terracotta-pale);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 26px;
      flex-shrink: 0;
    }
    .mobile-recipe-info { flex: 1; min-width: 0; }
    .mobile-recipe-title { font-family: 'Playfair Display', serif; font-size: 15px; color: var(--ink); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mobile-recipe-time { font-size: 12px; color: var(--ink-muted); margin-top: 3px; }
    .mobile-recipe-arrow { color: var(--terracotta); font-size: 18px; opacity: 0.5; }

    /* Mobile detail */
    .mobile-detail { display: flex; flex-direction: column; }
    .mobile-detail-back {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 14px 20px;
      background: var(--ink);
      cursor: pointer;
      border: none;
      width: 100%;
      text-align: left;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    .mobile-detail-back span { color: var(--cream); font-size: 14px; font-family: 'DM Sans', sans-serif; }
    .mobile-detail-hero {
      height: 200px;
      background: var(--terracotta-pale);
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
      flex-shrink: 0;
    }
    .mobile-detail-hero.no-image {
      height: 100px;
    }
    .mobile-detail-hero img { width: 100%; height: 100%; object-fit: cover; }
    .mobile-detail-hero-emoji { font-size: 72px; }
    .mobile-detail-body { padding: 20px; }
    .mobile-detail-title { font-family: 'Playfair Display', serif; font-size: 24px; color: var(--ink); line-height: 1.2; margin-bottom: 8px; }
    .mobile-detail-desc { font-size: 14px; color: var(--ink-light); line-height: 1.7; margin-bottom: 16px; }
    .mobile-meta-row { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 24px; }
    .mobile-meta-pill { background: white; border: 1px solid var(--border); border-radius: 8px; padding: 6px 12px; }
    .mobile-meta-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--ink-muted); }
    .mobile-meta-value { font-size: 13px; font-weight: 600; color: var(--ink); }
    .mobile-section-title { font-family: 'Playfair Display', serif; font-size: 17px; color: var(--ink); margin-bottom: 12px; margin-top: 20px; }
    .mobile-ingredient { padding: 9px 0; border-bottom: 1px solid var(--border); font-size: 14px; color: var(--ink-light); display: flex; gap: 10px; }
    .mobile-ingredient-amount { color: var(--terracotta); font-weight: 600; min-width: 64px; text-align: right; flex-shrink: 0; font-size: 13px; }
    .mobile-step { display: flex; gap: 12px; margin-bottom: 14px; }
    .mobile-step-num { width: 26px; height: 26px; border-radius: 50%; background: var(--terracotta); color: white; font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; }
    .mobile-step-text { font-size: 14px; color: var(--ink-light); line-height: 1.7; }
    .mobile-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 20px; }
    .mobile-tag { padding: 4px 12px; background: var(--sage-light); color: var(--sage); border-radius: 20px; font-size: 12px; }
    .mobile-notes { margin-top: 20px; padding: 14px 16px; background: var(--terracotta-pale); border-radius: 10px; border-left: 3px solid var(--terracotta); }
    .mobile-notes-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--terracotta); margin-bottom: 6px; font-weight: 600; }
    .mobile-notes-text { font-size: 13px; color: var(--ink-light); line-height: 1.6; }
    .mobile-action-row { display: flex; gap: 8px; margin-bottom: 16px; }
    .mobile-btn-ig { display: flex; align-items: center; gap: 6px; padding: 8px 14px; background: white; border: 1.5px solid var(--border); border-radius: 8px; font-size: 13px; color: var(--ink-light); cursor: pointer; text-decoration: none; }
    .mobile-btn-delete { display: flex; align-items: center; gap: 6px; padding: 8px 14px; background: white; border: 1.5px solid var(--border); border-radius: 8px; font-size: 13px; color: var(--ink-muted); cursor: pointer; margin-left: auto; }

    /* ── Mobile edit form ── */
    .mobile-edit-form { display: flex; flex-direction: column; gap: 0; padding-bottom: 16px; }
    .mobile-edit-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 16px; }
    .mobile-edit-input {
      font-family: 'DM Sans', sans-serif; font-size: 16px; color: var(--ink);
      background: white; border: 1.5px solid var(--border); border-radius: 10px;
      padding: 11px 14px; outline: none; width: 100%;
      -webkit-appearance: none; appearance: none;
    }
    .mobile-edit-input:focus { border-color: var(--terracotta); }
    .mobile-edit-title { font-family: 'Playfair Display', serif; font-size: 1.3rem; }
    .mobile-edit-textarea {
      font-family: 'DM Sans', sans-serif; font-size: 15px; color: var(--ink);
      background: white; border: 1.5px solid var(--border); border-radius: 10px;
      padding: 11px 14px; outline: none; width: 100%; resize: none; line-height: 1.5;
      -webkit-appearance: none; appearance: none;
    }
    .mobile-edit-textarea:focus { border-color: var(--terracotta); }
    .mobile-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
    .mobile-edit-section-label {
      font-family: 'Playfair Display', serif; font-size: 1.1rem; color: var(--ink);
      margin: 8px 0 12px; padding-top: 16px; border-top: 1px solid var(--border);
    }
    .mobile-edit-ing-row { margin-bottom: 10px; display: flex; flex-direction: column; gap: 6px; }
    .mobile-edit-ing-top { display: flex; gap: 8px; align-items: center; }
    .mobile-edit-ing-amount { flex: 0 0 72px; }
    .mobile-edit-ing-unit { flex: 0 0 80px; }
    .mobile-edit-remove {
      background: none; border: none; cursor: pointer; color: var(--ink-muted);
      font-size: 22px; line-height: 1; padding: 6px 8px; border-radius: 8px;
      flex-shrink: 0; margin-left: auto;
    }
    .mobile-edit-remove:active { color: #c44040; background: #fdf0f0; }
    .mobile-edit-step-row { display: grid; grid-template-columns: auto 1fr auto; gap: 8px; align-items: flex-start; margin-bottom: 10px; }
    .mobile-edit-step-text { min-height: 80px; }
    .mobile-edit-actions { display: flex; gap: 10px; padding: 16px 0 8px; border-top: 1px solid var(--border); margin-top: 8px; }

    /* Mobile add tab */
    .mobile-add-tab { padding: 24px 20px; }
    .mobile-add-title { font-family: 'Playfair Display', serif; font-size: 22px; color: var(--ink); margin-bottom: 6px; }
    .mobile-add-subtitle { font-size: 14px; color: var(--ink-muted); margin-bottom: 24px; }
    .mobile-url-input { width: 100%; padding: 14px 16px; border: 1.5px solid var(--border); border-radius: 12px; font-family: 'DM Sans', sans-serif; font-size: 15px; outline: none; background: white; color: var(--ink); margin-bottom: 12px; }
    .mobile-url-input:focus { border-color: var(--terracotta); }
    .mobile-btn-add { width: 100%; padding: 16px; background: var(--terracotta); border: none; border-radius: 12px; color: white; font-family: 'DM Sans', sans-serif; font-size: 16px; font-weight: 600; cursor: pointer; }
    .mobile-btn-add:disabled { opacity: 0.6; }
    .mobile-add-hint { margin-top: 20px; padding: 16px; background: var(--terracotta-pale); border-radius: 12px; }
    .mobile-add-hint-title { font-size: 12px; color: var(--terracotta); font-weight: 600; margin-bottom: 6px; }
    .mobile-add-hint-text { font-size: 13px; color: var(--ink-light); line-height: 1.6; }


    /* Account menu */
    .account-menu-overlay { position: fixed; inset: 0; z-index: 1000; display: flex; flex-direction: column; justify-content: flex-end; }
    .account-menu-backdrop { position: absolute; inset: 0; background: rgba(26,18,9,0.5); backdrop-filter: blur(2px); }
    .account-menu-sheet {
      position: relative; z-index: 1; background: white;
      border-radius: 20px 20px 0 0; padding: 20px 20px calc(20px + env(safe-area-inset-bottom,0px));
      animation: slideInUp 0.25s ease;
    }
    .account-menu-header { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid var(--border); }
    .account-menu-avatar { width: 48px; height: 48px; border-radius: 50%; border: 2px solid var(--terracotta); object-fit: cover; }
    .account-menu-avatar-placeholder { width: 48px; height: 48px; border-radius: 50%; background: var(--terracotta); display: flex; align-items: center; justify-content: center; font-family: 'Playfair Display', serif; font-size: 18px; color: white; flex-shrink: 0; }
    .account-menu-name { font-family: 'Playfair Display', serif; font-size: 16px; color: var(--ink); }
    .account-menu-email { font-size: 12px; color: var(--ink-muted); margin-top: 2px; }
    .account-menu-item { display: flex; align-items: center; gap: 14px; padding: 14px 4px; border-bottom: 1px solid var(--border); cursor: pointer; width: 100%; background: none; border-left: none; border-right: none; border-top: none; text-align: left; -webkit-tap-highlight-color: transparent; }
    .account-menu-item:last-child { border-bottom: none; padding-bottom: 0; }
    .account-menu-item-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
    .account-menu-item-icon.logout { background: var(--terracotta-pale); color: var(--terracotta); }
    .account-menu-item-icon.danger { background: #fdf0f0; color: #c44040; }
    .account-menu-item-text { font-family: 'DM Sans', sans-serif; font-size: 14.5px; font-weight: 500; }
    .account-menu-item-text.danger { color: #c44040; }

    .account-menu-item-sub { font-size: 11.5px; color: var(--ink-muted); margin-top: 1px; }

    /* Mobile search tab */
    .mobile-search-tab { padding: 20px; min-height: 0; }
    .mobile-search-input { width: 100%; padding: 14px 16px 14px 40px; border: 1.5px solid var(--border); border-radius: 12px; font-family: 'DM Sans', sans-serif; font-size: 15px; outline: none; background: white; color: var(--ink); box-sizing: border-box; }
    .mobile-search-input:focus { border-color: var(--terracotta); }
    .mobile-search-wrapper { position: relative; margin-bottom: 20px; }
    .mobile-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--ink-muted); font-size: 16px; }

    /* Mobile photo upload */
    .mobile-photo-section { margin: 16px 0; }
    .mobile-btn-photo { display: flex; align-items: center; gap: 8px; padding: 10px 16px; background: white; border: 1.5px solid var(--border); border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; color: var(--ink-muted); cursor: pointer; width: 100%; }
    .mobile-btn-photo:active { background: var(--terracotta-pale); border-color: var(--terracotta); }
  }

  @media (min-width: 769px) {
    .mobile-shell { display: none; }
    .desktop-shell { display: flex; }
  }

  @media (max-width: 768px) {
    .desktop-shell { display: none !important; }
  }
`;

// ─── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user } = useUser();
  const isAuthenticated = isSignedIn;
  const authLoading = !authLoaded;
  const [recipes, setRecipes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState(null); // { type: 'loading'|'error'|'success', message }
  const [loading, setLoading] = useState(true);
  const [mobileTab, setMobileTab] = useState('home');
  const [mobileSelectedId, setMobileSelectedId] = useState(null);
  const [mobileSearch, setMobileSearch] = useState('');
  const [desktopSearch, setDesktopSearch] = useState('');
  const [desktopBrowse, setDesktopBrowse] = useState('recipes'); // 'recipes' | 'creators'
  const [cookModeRecipe, setCookModeRecipe] = useState(null);
  const [showShopping, setShowShopping] = useState(false);
  const [showSub, setShowSub] = useState(null); // recipe
  const [showWcim, setShowWcim] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [desktopView, setDesktopView] = useState('home');
  const [darkMode, setDarkMode] = useState(false);
  const [newRecipeId, setNewRecipeId] = useState(null); // tracks freshly imported recipe for edit nudge

  useEffect(() => {
    if (isAuthenticated) loadRecipes();
    else if (!authLoading) setLoading(false);
  }, [isAuthenticated, authLoading]);

  async function loadRecipes() {
    try {
      const res = await fetch(`${API_URL}/api/recipes`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setRecipes(list);
      if (list.length > 0 && !selectedId) setSelectedId(list[0].id);
    } catch (e) {
      console.error('Failed to load recipes:', e);
    } finally {
      setLoading(false);
    }
  }

  const [importStep, setImportStep] = useState(null);   // null | 'fetch'|'transcribe'|'extract'|'save'
  const [importError, setImportError] = useState(null);

  async function handleAddRecipe() {
    if (!url.trim()) return null;
    if (!url.includes('instagram.com')) {
      setStatus({ type: 'error', message: 'Please enter a valid Instagram URL' });
      return null;
    }

    setImportError(null);
    setImportStep('fetch');
    setStatus({ type: 'loading', message: 'Importing...' });

    // Progress through steps with timing hints
    const t1 = setTimeout(() => setImportStep('transcribe'), 3500);
    const t2 = setTimeout(() => setImportStep('extract'), 22000);

    try {
      const res = await fetch(`${API_URL}/api/recipes/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      clearTimeout(t1); clearTimeout(t2);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to process recipe');

      setImportStep('save');
      await new Promise(r => setTimeout(r, 600)); // brief "saving" moment

      setRecipes(prev => [data.recipe, ...prev]);
      setSelectedId(data.recipe.id);
      setUrl('');
      setStatus({ type: 'success', message: `"${data.recipe.title}" added!` });
      setImportStep(null);
      setTimeout(() => setStatus(null), 3000);
      return data.recipe.id;
    } catch (e) {
      clearTimeout(t1); clearTimeout(t2);
      setImportStep('error');
      setImportError(e.message);
      setStatus({ type: 'error', message: e.message });
      return null;
    }
  }

  async function handleAddRecipeFromHome() {
    if (!url.trim()) return;
    if (!url.includes('instagram.com')) {
      setStatus({ type: 'error', message: 'Please enter a valid Instagram URL' });
      return;
    }
    const recipeId = await handleAddRecipe();
    if (recipeId) {
      setDesktopView('recipes');
    }
  }

  function handleUpdatePhoto(id, thumbnailUrl) {
    setRecipes(prev => prev.map(r => r.id === id ? { ...r, thumbnailUrl } : r));
  }

  async function handleUpdateRecipe(id, fields) {
    try {
      const res = await fetch(`${API_URL}/api/recipes/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setRecipes(prev => prev.map(r => r.id === id ? data.recipe : r));
    } catch (err) {
      alert('Failed to save changes: ' + err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this recipe from your collection?')) return;
    await fetch(`${API_URL}/api/recipes/${id}`, { method: 'DELETE' });
    setRecipes(prev => prev.filter(r => r.id !== id));
    if (selectedId === id) setSelectedId(recipes.find(r => r.id !== id)?.id || null);
  }

  const selectedRecipe = recipes.find(r => r.id === selectedId);

  if (authLoading) {
    return (
      <>
        <style>{styles}</style>
        <div className="login-screen">
          <div className="login-logo"><FullLogo height={28} /></div>
          <div className="login-loading">Loading...</div>
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <style>{styles}</style>
        <div className="login-screen">
          <div className="login-logo"><FullLogo height={28} /></div>
          <p className="login-tagline">Your family recipe collection, powered by Instagram.</p>
          <div style={{width:'100%',maxWidth:'400px'}}>
            <SignIn routing="hash" />
          </div>
        </div>
      </>
    );
  }

  function handleSelectRecipe(id) {
    setSelectedId(id);
    setDesktopView('recipes');
  }

  const firstName = user?.firstName || user?.fullName?.split(' ')[0] || 'there';
  const creators = getCreators(recipes);

  return (
    <>
      <style>{styles}</style>

      {/* ══════════════════════════════════
          DESKTOP SHELL
      ══════════════════════════════════ */}
      <div className="desktop-shell">
        {/* Emoji font preloader — renders all emoji once off-screen to prime the font cache */}
        <div className="emoji-preloader" aria-hidden="true">
          🍗🍔🍝🍰🍫🥗🍲🍕🌮🍣🍛🐟🍳🥣🥤🍞🥩🥓🥦🍽
        </div>
        {/* Left nav */}
        <DesktopNav
          collapsed={navCollapsed}
          onToggle={() => setNavCollapsed(c => !c)}
          desktopView={desktopView}
          desktopBrowse={desktopBrowse}
          setDesktopView={setDesktopView}
          setSelectedId={setSelectedId}
          setDesktopBrowse={setDesktopBrowse}
          onShopping={() => setShowShopping(true)}
          onWcim={() => setShowWcim(true)}
          user={user}
          recipeCount={recipes.length}
          creatorCount={creators.length}
          darkMode={darkMode}
          onToggleDark={() => setDarkMode(d => !d)}
        />

        {/* Content */}
        <div className={`desktop-content${darkMode ? ' dark-mode' : ''}`}>
          {desktopView === 'home' ? (
            <DesktopHomeScreen
              firstName={firstName}
              recipes={recipes}
              creators={creators}
              url={url}
              setUrl={setUrl}
              status={status}
              onImport={handleAddRecipeFromHome}
              onBrowse={() => { setDesktopView('recipes'); setDesktopBrowse('recipes'); }}
              onInspire={() => setShowWcim(true)}
              loading={loading}
              darkMode={darkMode}
            />
          ) : (
            <div className="app-layout">
              {/* Recipe list sidebar */}
              <aside className="sidebar">
                <div className="add-recipe-section">
                  <h2>Add from Instagram</h2>
                  <div className="url-input-row">
                    <input
                      className="url-input"
                      type="url"
                      placeholder="Paste Instagram URL..."
                      value={url}
                      onChange={e => setUrl(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddRecipe()}
                      disabled={status?.type === 'loading'}
                    />
                    <button
                      className="btn-add"
                      onClick={handleAddRecipe}
                      disabled={status?.type === 'loading' || !url.trim()}
                    >
                      {status?.type === 'loading' ? '...' : 'Add'}
                    </button>
                  </div>
                  {status && (
                    <div className={`processing-status ${status.type}`}>
                      {status.type === 'loading' && <div className="spinner" />}
                      {status.type === 'success' && ''}
                      {status.type === 'error' && ''}
                      {status.message}
                    </div>
                  )}
                </div>

                {/* Search */}
                <div style={{ padding: '16px 20px 0' }}>
                  <div style={{ position: 'relative' }}>
                    <input type="text" placeholder={desktopBrowse === 'creators' ? 'Search creators...' : 'Search recipes...'}
                      value={desktopSearch} onChange={e => setDesktopSearch(e.target.value)}
                      className='sidebar-search-input' style={{ width: '100%', padding: '8px 12px 8px 36px', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', background: 'var(--warm-white,white)', color: 'var(--ink)', outline: 'none', boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = 'var(--terracotta)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-muted)', width: 16, height: 16, display: 'flex' }}><IconSearch /></span>
                  </div>
                </div>

                <div className="recipe-list-header" style={{ paddingTop: 12 }}>
                  {desktopBrowse === 'creators' ? 'Browse by Creator' : desktopSearch ? `${recipes.filter(r => matchesSearch(r, desktopSearch)).length} results` : 'Your Collection'}
                </div>

                <div className="recipe-list">
                  {loading ? (
                    <SkeletonSidebar />
                  ) : desktopBrowse === 'creators' ? (
                    <DesktopCreatorList
                      creators={getCreators(recipes).filter(c => !desktopSearch || (c.username||'').toLowerCase().includes(desktopSearch.toLowerCase()) || (c.displayName||'').toLowerCase().includes(desktopSearch.toLowerCase()))}
                      recipes={recipes} selectedId={selectedId} onSelect={handleSelectRecipe}
                    />
                  ) : recipes.length === 0 ? (
                    <div className="empty-collection">
                      <div style={{display:"flex",justifyContent:"center",marginBottom:12,color:"var(--ink-muted)",opacity:0.4,fontSize:36}}><IconBook /></div>
                      <p>Import a recipe from the home screen to get started!</p>
                    </div>
                  ) : (
                    recipes.filter(r => !desktopSearch || matchesSearch(r, desktopSearch)).map(recipe => (
                      <div key={recipe.id} className={`recipe-card ${selectedId === recipe.id ? 'active' : ''}`} onClick={() => handleSelectRecipe(recipe.id)}>
                        <RecipeThumb recipe={recipe} />
                        <div className="recipe-card-info">
                          <div className="recipe-card-title">{recipe.title}</div>
                          <div className="recipe-card-meta">{recipe.totalTime && <span>{recipe.totalTime}</span>}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </aside>

              {/* Main detail */}
              <main className="main-content">
                {loading ? (
                  <SkeletonDetail />
                ) : !selectedRecipe ? (
                  <div className="welcome-screen">
                    <div style={{display:"flex",justifyContent:"center",marginBottom:12,color:"var(--ink-muted)",opacity:0.4,fontSize:36}}><IconSparkles /></div>
                    <h2>Select a recipe</h2>
                    <p>Choose a recipe from the list to view its ingredients and steps.</p>
                  </div>
                ) : (
                  <RecipeDetail recipe={selectedRecipe} onDelete={handleDelete} onUpdatePhoto={handleUpdatePhoto} onUpdateRecipe={handleUpdateRecipe} onCookMode={setCookModeRecipe} onSub={setShowSub} isNew={newRecipeId === selectedRecipe?.id} onDismissNew={() => setNewRecipeId(null)} />
                )}
              </main>
            </div>
          )}
        </div>
      </div>

      {/* ── Global overlays ── */}
      {importStep && (
        <ImportOverlay
          step={importStep === 'error' ? 'save' : importStep}
          error={importError}
          onDismiss={() => { setImportStep(null); setImportError(null); setStatus(null); }}
        />
      )}
      {cookModeRecipe && <CookMode recipe={cookModeRecipe} onClose={() => setCookModeRecipe(null)} />}
      {showShopping && <ShoppingList recipes={recipes} onClose={() => setShowShopping(false)} darkMode={darkMode} />}
      {showSub && <AiChatPanel config={buildSubConfig(showSub)} onClose={() => setShowSub(null)} darkMode={darkMode} />}
      {showWcim && <AiChatPanel config={buildWcimConfig(recipes)} onClose={() => setShowWcim(false)} darkMode={darkMode} />}

      {/* ══════════════════════════════════
          MOBILE SHELL
      ══════════════════════════════════ */}
      <MobileShell
        loading={loading}
        recipes={recipes}
        mobileTab={mobileTab}
        setMobileTab={setMobileTab}
        mobileSelectedId={mobileSelectedId}
        setMobileSelectedId={setMobileSelectedId}
        mobileSearch={mobileSearch}
        setMobileSearch={setMobileSearch}
        url={url}
        setUrl={setUrl}
        status={status}
        handleAddRecipe={handleAddRecipe}
        handleDelete={handleDelete}
        handleUpdatePhoto={handleUpdatePhoto}
        handleUpdateRecipe={handleUpdateRecipe}
        newRecipeId={newRecipeId}
        setNewRecipeId={setNewRecipeId}
        user={user}
        firstName={firstName}
        onCookMode={setCookModeRecipe}
        onSub={setShowSub}
        onShopping={() => setShowShopping(true)}
        onWcim={() => setShowWcim(true)}
      />
    </>
  );
}

// ─── Remixicon wrappers (uses CDN font loaded in index.html) ──────────────────
// Usage: <Ri name="home-3" /> renders <i class="ri-home-3-line" />
// All icons use -line variant by default for consistency

function Ri({ name, fill, size, style, className }) {
  const variant = fill ? 'fill' : 'line';
  return <i className={`ri-${name}-${variant}${className ? ' ' + className : ''}`}
    style={{ fontSize: size || '1em', lineHeight: 1, display: 'inline-flex', alignItems: 'center', ...style }} />;
}

// Named aliases used throughout the app
const IconHome       = (p) => <Ri name="home-4" {...p} />;
const IconBook       = (p) => <Ri name="book-2" {...p} />;
const IconUsers      = (p) => <Ri name="group" {...p} />;
const IconCart       = (p) => <Ri name="shopping-cart-2" {...p} />;
const IconSparkles   = (p) => <Ri name="sparkling" {...p} />;
const IconSearch     = (p) => <Ri name="search" {...p} />;
const IconPlus       = (p) => <Ri name="add" {...p} />;
const IconLink       = (p) => <Ri name="link" {...p} />;
const IconInfo       = (p) => <Ri name="information" {...p} />;
const IconArrowUp    = (p) => <Ri name="arrow-up" {...p} />;
const IconChevronRight = (p) => <Ri name="arrow-right-s" {...p} />;
const IconCollapseLeft = (p) => <Ri name="sidebar-fold" {...p} />;
const IconExpandRight  = (p) => <Ri name="sidebar-unfold" {...p} />;
const IconCook       = (p) => <Ri name="timer" {...p} />;
const IconRefresh    = (p) => <Ri name="arrow-left-right" {...p} />;
const IconTrash      = (p) => <Ri name="delete-bin-6" {...p} />;
const IconInstagram  = (p) => <Ri name="instagram" {...p} />;
const IconCamera     = (p) => <Ri name="camera-3" {...p} />;
const IconX          = (p) => <Ri name="close" {...p} />;
const IconArrowLeft  = (p) => <Ri name="arrow-left" {...p} />;
const IconCheck      = (p) => <Ri name="check" {...p} />;
const IconShare      = (p) => <Ri name="share-forward" {...p} />;
const IconMoon       = (p) => <Ri name="moon" {...p} />;
const IconSun        = (p) => <Ri name="sun" {...p} />;

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ─── Desktop Nav Sidebar ───────────────────────────────────────────────────────

function DesktopNav({ collapsed, onToggle, desktopView, desktopBrowse, setDesktopView, setSelectedId, setDesktopBrowse, onShopping, onWcim, user, recipeCount, creatorCount, darkMode, onToggleDark }) {
  const [showAccount, setShowAccount] = React.useState(false);
  const accountBtnRef = React.useRef(null);
  const initials = (user?.fullName || user?.firstName || '?').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();
  const mainItems = [
    { id: 'home',     label: 'Home',       icon: <IconHome />,  browse: null },
    { id: 'recipes',  label: 'My Recipes', icon: <IconBook />,  browse: 'recipes', count: recipeCount },
    { id: 'creators', label: 'Creators',   icon: <IconUsers />, browse: 'creators', count: creatorCount },
  ];
  const toolItems = [
    { id: 'shopping', label: 'Shopping List',     icon: <IconCart />,     action: onShopping },
    { id: 'wcim',     label: 'What Can I Make?',  icon: <IconSparkles />, action: onWcim },
  ];

  function handleNav(item) {
    if (item.action) { item.action(); return; }
    if (item.id === 'home') { setDesktopView('home'); }
    else { setDesktopView('recipes'); setDesktopBrowse(item.browse); }
  }

  function isActive(item) {
    if (item.id === 'home') return desktopView === 'home';
    if (desktopView !== 'recipes') return false;
    return desktopBrowse === item.browse;
  }

  return (
    <nav className={`desktop-nav ${collapsed ? 'collapsed' : 'expanded'}`}>
      {/* Expanded header */}
      <div className="nav-header-expanded">
        <FullLogo height={22} />
        <button className="nav-toggle-btn" onClick={onToggle} title="Collapse">
          <IconCollapseLeft />
        </button>
      </div>
      {/* Collapsed header — icon only */}
      <div className="nav-header-collapsed">
        <button className="nav-toggle-btn" onClick={onToggle} title="Expand" style={{ width: 'auto', height: 'auto', padding: 4 }}>
          <FullLogo height={22} iconOnly />
        </button>
      </div>

      <div className="nav-inner-scroll">
        <div className="nav-group">
          {mainItems.map(item => (
            <button key={item.id}
              className={`nav-item ${isActive(item) ? 'active' : ''}`}
              data-label={item.label}
              onClick={() => handleNav(item)}>
              {item.icon}
              <span className="nav-item-label">{item.label}</span>
              {item.count !== undefined && item.count > 0 && (
                <span className="nav-count">{item.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="nav-divider" />

        <div className="nav-group">
          {toolItems.map(item => (
            <button key={item.id}
              className="nav-item"
              data-label={item.label}
              onClick={() => handleNav(item)}>
              {item.icon}
              <span className="nav-item-label">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

        <div className="nav-footer">
        <button
          className="nav-item"
          data-label={darkMode ? 'Light mode' : 'Dark mode'}
          onClick={onToggleDark}
        >
          {darkMode ? <IconSun /> : <IconMoon />}
          <span className="nav-item-label">{darkMode ? 'Light mode' : 'Dark mode'}</span>
        </button>
        <div className="account-popover-wrap">
          <button className="nav-footer-user-btn" ref={accountBtnRef} onClick={() => setShowAccount(v => !v)}>
            {user?.imageUrl
              ? <img src={user.imageUrl} alt="" className="nav-footer-avatar" />
              : <div className="nav-footer-avatar">{initials}</div>
            }
            <div className="nav-footer-info">
              <div className="nav-footer-name">{user?.firstName || user?.fullName || 'You'}</div>
              <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>Account &amp; settings</div>
            </div>
            <i className="ri-settings-3-line nav-footer-settings-icon" />
          </button>
          {showAccount && <AccountPopover user={user} onClose={() => setShowAccount(false)} anchorRef={accountBtnRef} />}
        </div>
      </div>
    </nav>
  );
}

// ─── Desktop Home Screen ───────────────────────────────────────────────────────

function DesktopHomeScreen({ firstName, recipes, creators, url, setUrl, status, onImport, onBrowse, onInspire, loading, darkMode }) {
  const hasRecipes = recipes.length > 0;
  const timeGreeting = getTimeOfDay();

  return (
    <div className={`desktop-home${darkMode ? " dark-mode" : ""}`}>
      {hasRecipes ? (
        // Returning user
        <>
          <div className="home-greeting">
            <div className="home-eyebrow">{timeGreeting}</div>
            <div className="home-title">Hey, <em>{firstName}.</em></div>
            <div className="home-subtitle">What are you cooking today?</div>
          </div>

          <div className="home-import-card">
            <div className="home-import-label">
              <IconLink /> Import from Instagram
            </div>
            <div className="home-import-bar">
              <input
                className="home-import-input"
                type="url"
                placeholder="Paste an Instagram reel URL…"
                value={url}
                onChange={e => setUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onImport()}
                disabled={status?.type === 'loading'}
              />
              <button
                className={`home-import-icon-btn${status?.type === 'loading' ? ' loading' : ''}`}
                onClick={onImport}
                disabled={status?.type === 'loading' || !url.trim()}
                title="Import"
              >
                {status?.type === 'loading'
                  ? <Ri name="loader-4" />
                  : <Ri name="arrow-up" />}
              </button>
            </div>
            {status && status.type !== 'loading' && (
              <div className={`processing-status ${status.type}`} style={{ marginTop: 10, borderRadius: 100 }}>
                {status.message}
              </div>
            )}
            <div className="home-secondary-row">
              <button className="home-secondary-btn" onClick={onBrowse}>
                <IconBook /> Browse my recipes
              </button>
              <button className="home-secondary-btn" onClick={onInspire}>
                <IconSparkles /> Inspire me
              </button>
            </div>
          </div>
        </>
      ) : (
        // New user
        <>
          <div className="home-greeting">
            <div className="home-eyebrow">Welcome to Reel Recipes</div>
            <div className="home-title">Save your first <em>recipe.</em></div>
          </div>

          <div className="home-import-card">
            <div className="home-import-label">
              <IconLink /> Import from Instagram
            </div>
            <div className="home-import-bar">
              <input
                className="home-import-input"
                type="url"
                placeholder="Paste an Instagram reel URL…"
                value={url}
                onChange={e => setUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onImport()}
                disabled={status?.type === 'loading'}
              />
              <button
                className={`home-import-icon-btn${status?.type === 'loading' ? ' loading' : ''}`}
                onClick={onImport}
                disabled={status?.type === 'loading' || !url.trim()}
                title="Import"
              >
                {status?.type === 'loading'
                  ? <Ri name="loader-4" />
                  : <Ri name="arrow-up" />}
              </button>
            </div>
            {status && status.type !== 'loading' && (
              <div className={`processing-status ${status.type}`} style={{ marginTop: 10, borderRadius: 100 }}>
                {status.message}
              </div>
            )}
          </div>

          <div className="home-hint">
            <IconInfo />
            Paste any Instagram Reel link — we'll extract ingredients and steps automatically
          </div>
        </>
      )}
    </div>
  );
}

// ─── Skeleton Components ───────────────────────────────────────────────────────

function SkelBox({ w, h, style = {} }) {
  return <div className="skel" style={{ width: w, height: h, borderRadius: 6, flexShrink: 0, ...style }} />;
}

// Sidebar recipe card skeleton
function SkeletonCard() {
  return (
    <div className="skel-card">
      <div className="skel" style={{ width: 48, height: 48, borderRadius: 8, flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <SkelBox w="75%" h={13} />
        <SkelBox w="40%" h={11} />
      </div>
    </div>
  );
}

function SkeletonSidebar() {
  return (
    <div>
      {[1,2,3,4,5].map(i => <SkeletonCard key={i} />)}
    </div>
  );
}

// Full recipe detail skeleton
function SkeletonDetail() {
  return (
    <div className="recipe-detail" style={{ animation: 'none' }}>
      {/* Header */}
      <div className="recipe-detail-header">
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, paddingRight: 24 }}>
          <SkelBox w="85%" h={36} />
          <SkelBox w="60%" h={28} />
          <SkelBox w="95%" h={14} style={{ marginTop: 8 }} />
          <SkelBox w="80%" h={14} />
          <SkelBox w="70%" h={14} />
        </div>
        <div className="skel-detail-hero" />
      </div>

      {/* Meta row */}
      <div className="recipe-meta-row" style={{ marginTop: 24 }}>
        {[60, 72, 72, 80].map((w, i) => (
          <div key={i} className="meta-item">
            <SkelBox w={40} h={10} style={{ marginBottom: 6 }} />
            <SkelBox w={w} h={16} />
          </div>
        ))}
      </div>

      {/* Serving adjuster */}
      <div className="serving-adjuster" style={{ marginTop: 16 }}>
        <SkelBox w={120} h={14} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <SkelBox w={30} h={30} style={{ borderRadius: '50%' }} />
          <SkelBox w={20} h={20} />
          <SkelBox w={30} h={30} style={{ borderRadius: '50%' }} />
        </div>
      </div>

      {/* Body: ingredients + steps */}
      <div className="recipe-body" style={{ marginTop: 32 }}>
        {/* Ingredients */}
        <div>
          <SkelBox w={140} h={22} style={{ marginBottom: 20 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[80,65,90,55,75,60,85].map((w, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <SkelBox w={52} h={13} />
                <SkelBox w={`${w}%`} h={13} />
              </div>
            ))}
          </div>
        </div>

        {/* Steps */}
        <div>
          <SkelBox w={120} h={22} style={{ marginBottom: 20 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {[90,75,85,70,80].map((w, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <SkelBox w={32} h={32} style={{ borderRadius: '50%', flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
                  <SkelBox w={`${w}%`} h={13} />
                  {w > 75 && <SkelBox w={`${w - 20}%`} h={13} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Mobile recipe list skeleton
function SkeletonMobileList() {
  return (
    <div>
      {[1,2,3,4,5,6].map(i => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="skel" style={{ width: 52, height: 52, borderRadius: 10, flexShrink: 0 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <SkelBox w="70%" h={14} />
            <SkelBox w="35%" h={11} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Mobile recipe detail skeleton
function SkeletonMobileDetail() {
  return (
    <div className="mobile-detail">
      {/* Hero image */}
      <div className="skel" style={{ width: '100%', height: 200 }} />

      <div className="mobile-detail-body">
        {/* Title */}
        <SkelBox w="85%" h={28} style={{ marginBottom: 10 }} />
        <SkelBox w="65%" h={20} style={{ marginBottom: 20 }} />

        {/* Meta pills */}
        <div className="mobile-meta-row">
          {[64, 72, 72, 80].map((w, i) => (
            <div key={i} className="skel" style={{ width: w, height: 52, borderRadius: 8 }} />
          ))}
        </div>

        {/* Ingredients */}
        <SkelBox w={130} h={18} style={{ marginTop: 24, marginBottom: 16 }} />
        {[75,55,80,60,70].map((w, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <SkelBox w={52} h={13} />
            <SkelBox w={`${w}%`} h={13} />
          </div>
        ))}

        {/* Steps */}
        <SkelBox w={110} h={18} style={{ marginTop: 24, marginBottom: 16 }} />
        {[90,78,85,70].map((w, i) => (
          <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 18, alignItems: 'flex-start' }}>
            <SkelBox w={30} h={30} style={{ borderRadius: '50%', flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SkelBox w={`${w}%`} h={13} />
              {w > 78 && <SkelBox w={`${w - 18}%`} h={13} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Creator Avatar ────────────────────────────────────────────────────────────

function getInitials(displayName, username) {
  const name = displayName || username || '';
  // Strip handles like "| Healthy Recipes" — take first two proper words
  const words = name.replace(/[|@]/g, ' ').trim().split(/\s+/).filter(w => /^[A-Za-z]/.test(w));
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return '?';
}

function AvatarPlaceholder({ displayName, username, size }) {
  const initials = getInitials(displayName, username);
  // Generate a consistent warm colour from the name string
  const name = displayName || username || '';
  const hue = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  const bg = `hsl(${hue}, 45%, 60%)`;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 700, fontFamily: 'DM Sans, sans-serif',
      flexShrink: 0, border: '2px solid rgba(255,255,255,0.3)',
      userSelect: 'none', letterSpacing: '-0.5px',
    }}>
      {initials}
    </div>
  );
}

function CreatorAvatar({ displayName, username, storedUrl, size = 44 }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [username, storedUrl]);

  // Only use storedUrl if it's real base64 (not a logo/fallback URL from unavatar)
  const src = storedUrl?.startsWith('data:image/') ? storedUrl : null;

  if (src && !failed) {
    return <img src={src} alt="" onError={() => setFailed(true)}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--terracotta)', flexShrink: 0 }} />;
  }
  return <AvatarPlaceholder displayName={displayName} username={username} size={size} />;
}

// ─── Creator Strip ─────────────────────────────────────────────────────────────

function CreatorStrip({ recipe, mobile }) {
  const [avatarErr, setAvatarErr] = React.useState(false);
  React.useEffect(() => setAvatarErr(false), [recipe.id, recipe.creatorUsername]);

  if (mobile) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 0', marginBottom:12 }}>
        <CreatorAvatar displayName={recipe.creatorDisplayName} username={recipe.creatorUsername} storedUrl={recipe.creatorAvatarUrl} size={36} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:600, color:'var(--ink)' }}>{recipe.creatorDisplayName || recipe.creatorUsername}</div>
          {recipe.creatorUsername && <div style={{ fontSize:12, color:'var(--ink-muted)' }}>@{recipe.creatorUsername}</div>}
        </div>
        {recipe.creatorUsername && (
          <a href={`https://www.instagram.com/${recipe.creatorUsername}/`} target="_blank" rel="noopener noreferrer"
            style={{ fontSize:12, color:'var(--terracotta)', border:'1px solid var(--terracotta)', borderRadius:6, padding:'3px 8px', textDecoration:'none', flexShrink:0 }}>
            Profile
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="creator-strip">
      <CreatorAvatar displayName={recipe.creatorDisplayName} username={recipe.creatorUsername} storedUrl={recipe.creatorAvatarUrl} size={44} />
      <div>
        <div className="creator-name">{recipe.creatorDisplayName || recipe.creatorUsername}</div>
        {recipe.creatorUsername && <div className="creator-handle">@{recipe.creatorUsername}</div>}
      </div>
      {recipe.creatorUsername && (
        <a href={`https://www.instagram.com/${recipe.creatorUsername}/`} target="_blank" rel="noopener noreferrer" className="creator-follow">
          View profile
        </a>
      )}
    </div>
  );
}

// ─── Import Overlay ───────────────────────────────────────────────────────────

const IMPORT_STEPS = [
  { id: 'fetch',      label: 'Fetching Instagram post' },
  { id: 'transcribe', label: 'Transcribing video audio' },
  { id: 'extract',    label: 'Extracting recipe details' },
  { id: 'save',       label: 'Saving to your collection' },
];

function ImportOverlay({ step, error, onDismiss }) {
  const activeIdx = IMPORT_STEPS.findIndex(s => s.id === step);

  return (
    <div className="import-overlay">
      <div className="import-overlay-card">
        <div className="import-overlay-icon">
          {error ? <i className="ri-error-warning-line" /> : <i className="ri-instagram-line" />}
        </div>
        <div className="import-overlay-title">
          {error ? 'Import failed' : 'Importing recipe…'}
        </div>
        <div className="import-overlay-subtitle">
          {error ? 'Something went wrong. Check the URL and try again.' : 'This usually takes 20–60 seconds for video recipes.'}
        </div>

        {!error && (
          <div className="import-steps">
            {IMPORT_STEPS.map((s, i) => {
              const state = i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'pending';
              return (
                <div key={s.id} className="import-step">
                  <div className={`import-step-dot ${state}`}>
                    {state === 'done' ? <i className="ri-check-line" />
                      : state === 'active' ? <i className="ri-loader-4-line" style={{ animation: 'spin 0.8s linear infinite' }} />
                      : <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor', display: 'block' }} />}
                  </div>
                  <span className={`import-step-label ${state}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <>
            <div className="import-overlay-error">{error}</div>
            <button className="import-overlay-error-btn" onClick={onDismiss}>Try again</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Recipe Thumb ─────────────────────────────────────────────────────────────

const RecipeThumb = React.memo(function RecipeThumb({ recipe }) {
  const [err, setErr] = React.useState(false);
  React.useEffect(() => setErr(false), [recipe.id, recipe.thumbnailUrl]);
  if (recipe.thumbnailUrl && !err) {
    return <img src={proxyImage(recipe.thumbnailUrl)} alt="" className="recipe-thumb" onError={() => setErr(true)} />;
  }
  return <div className="recipe-thumb-placeholder">{recipeEmoji(recipe)}</div>;
}, (prev, next) => prev.recipe.id === next.recipe.id && prev.recipe.thumbnailUrl === next.recipe.thumbnailUrl);

// ─── Recipe Detail ─────────────────────────────────────────────────────────────

function RecipeDetail({ recipe, onDelete, onUpdatePhoto, onUpdateRecipe, onCookMode, onSub, isNew, onDismissNew }) {
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);
  const [currentServings, setCurrentServings] = React.useState(recipe.baseServings || null);
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    setImgError(false);
    setCurrentServings(recipe.baseServings || null);
    setEditing(false);
    setDraft(null);
  }, [recipe.id]);

  function startEdit() {
    setDraft({
      title: recipe.title || '',
      description: recipe.description || '',
      prepTime: recipe.prepTime || '',
      cookTime: recipe.cookTime || '',
      totalTime: recipe.totalTime || '',
      servings: recipe.servings || '',
      ingredients: (recipe.ingredients || []).map(ing => ({ ...ing })),
      steps: (recipe.steps || []).map(s => ({ ...s })),
      notes: recipe.notes || '',
    });
    setEditing(true);
    if (onDismissNew) onDismissNew();
  }

  function cancelEdit() {
    setEditing(false);
    setDraft(null);
  }

  async function saveEdit() {
    if (!draft) return;
    setSaving(true);
    try {
      await onUpdateRecipe(recipe.id, {
        ...recipe,
        title: draft.title,
        description: draft.description,
        prepTime: draft.prepTime,
        cookTime: draft.cookTime,
        totalTime: draft.totalTime,
        servings: draft.servings,
        baseServings: recipe.baseServings,
        ingredients: draft.ingredients.filter(i => i.item?.trim()),
        steps: draft.steps.filter(s => s.instruction?.trim()).map((s, idx) => ({ ...s, step: idx + 1 })),
        notes: draft.notes,
      });
      setEditing(false);
      setDraft(null);
    } finally {
      setSaving(false);
    }
  }

  function updateDraftIng(idx, field, value) {
    setDraft(d => ({ ...d, ingredients: d.ingredients.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing) }));
  }
  function addIng() {
    setDraft(d => ({ ...d, ingredients: [...d.ingredients, { amount: '', unit: '', item: '', notes: '' }] }));
  }
  function removeIng(idx) {
    setDraft(d => ({ ...d, ingredients: d.ingredients.filter((_, i) => i !== idx) }));
  }
  function updateDraftStep(idx, value) {
    setDraft(d => ({ ...d, steps: d.steps.map((s, i) => i === idx ? { ...s, instruction: value } : s) }));
  }
  function addStep() {
    setDraft(d => ({ ...d, steps: [...d.steps, { instruction: '', step: d.steps.length + 1 }] }));
  }
  function removeStep(idx) {
    setDraft(d => ({ ...d, steps: d.steps.filter((_, i) => i !== idx) }));
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          const maxSize = 1600;
          let { width, height } = img;
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(objectUrl);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = reject;
        img.src = objectUrl;
      });
      const res = await fetch(`${API_URL}/api/recipes/${recipe.id}/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      onUpdatePhoto(recipe.id, data.thumbnailUrl);
      setImgError(false);
    } catch (err) {
      alert('Failed to upload photo: ' + err.message);
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  }

  const hasPhoto = recipe.thumbnailUrl && !imgError;

  return (
    <div className={`recipe-detail${editing ? ' editing' : ''}`}>
      <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handlePhotoUpload} />

      {/* New recipe edit nudge */}
      {isNew && !editing && (
        <div className="edit-nudge">
          <span className="edit-nudge-text"><strong>Just imported!</strong> Does everything look right?</span>
          <button className="edit-nudge-btn" onClick={startEdit}>Review &amp; edit</button>
          <button className="edit-nudge-dismiss" onClick={onDismissNew} title="Dismiss">×</button>
        </div>
      )}

      {/* Title + Photo */}
      <div className="recipe-detail-header">
        <div style={{ flex: 1 }}>
          {editing ? (
            <input className="edit-title-input" value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="Recipe title" />
          ) : (
            <h1 className="recipe-detail-title">{recipe.title}</h1>
          )}
          {editing ? (
            <textarea className="edit-textarea" style={{marginTop:10, minHeight:56}} value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} placeholder="Short description (optional)" />
          ) : (
            recipe.description && <p className="recipe-detail-description">{recipe.description}</p>
          )}
        </div>
        <div className="photo-upload-area">
          {uploadingPhoto ? (
            <div className="recipe-image-wrapper" style={{cursor:'default'}}>
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',width:'100%',height:'100%',gap:10}}>
                <div className="spinner" style={{width:24,height:24,borderWidth:3,borderColor:'var(--terracotta)',borderTopColor:'transparent'}} />
                <span style={{fontSize:'0.75rem',color:'var(--ink-muted)'}}>Uploading…</span>
              </div>
            </div>
          ) : (
            <div className="recipe-image-wrapper" onClick={() => fileInputRef.current.click()} style={{cursor:'pointer'}}>
              {hasPhoto ? (
                <img src={proxyImage(recipe.thumbnailUrl)} alt={recipe.title} className="recipe-image" onError={() => setImgError(true)} />
              ) : (
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',width:'100%',height:'100%',fontSize:'5rem',background:'var(--terracotta-pale)'}}>
                  {recipeEmoji(recipe)}
                </div>
              )}
              <div className="photo-overlay">
                <span style={{fontSize:13,display:'flex',alignItems:'center',gap:5}}><IconCamera style={{fontSize:14}} /> {hasPhoto ? 'Change photo' : 'Add photo'}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="recipe-toolbar" style={{ marginBottom: 20 }}>
        {!editing && onCookMode && <button className="btn-cook-mode" style={{display:"flex",alignItems:"center",gap:8}} onClick={() => onCookMode(recipe)}><IconCook style={{fontSize:15}} /> Cook Mode</button>}
        {!editing && onSub && <button className="btn-toolbar" style={{display:"flex",alignItems:"center",gap:6}} onClick={() => onSub(recipe)}><IconRefresh style={{fontSize:14}} /> Substitutions</button>}
        <button className={`btn-edit${editing ? ' active' : ''}`} onClick={editing ? cancelEdit : startEdit} style={{marginLeft:'auto'}}>
          <i className={`ri-${editing ? 'close' : 'pencil'}-line`} style={{fontSize:13}} />
          {editing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      {/* Meta row */}
      {editing ? (
        <div className="edit-meta-grid">
          {[['Serves','servings'],['Prep time','prepTime'],['Cook time','cookTime'],['Total time','totalTime']].map(([label, key]) => (
            <div className="edit-field" key={key} style={{marginBottom:0}}>
              <label className="edit-label">{label}</label>
              <input className="edit-input" value={draft[key]} onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} placeholder={label} />
            </div>
          ))}
        </div>
      ) : (
        <div className="recipe-meta-row">
          {recipe.servings && (
            <div className="meta-item">
              <span className="meta-label">Serves</span>
              <span className="meta-value">{currentServings ?? recipe.servings}</span>
            </div>
          )}
          {recipe.prepTime && <div className="meta-item"><span className="meta-label">Prep</span><span className="meta-value">{recipe.prepTime}</span></div>}
          {recipe.cookTime && <div className="meta-item"><span className="meta-label">Cook</span><span className="meta-value">{recipe.cookTime}</span></div>}
          {recipe.totalTime && <div className="meta-item"><span className="meta-label">Total</span><span className="meta-value">{recipe.totalTime}</span></div>}
          {recipe.source && (
            <div className="meta-item">
              <span className="meta-label">Source</span>
              <span className="meta-value" style={{fontSize:'0.82rem'}}>{recipe.source === 'transcription' ? 'Video' : 'Caption'}</span>
            </div>
          )}
        </div>
      )}

      {/* Serving adjuster */}
      {recipe.baseServings && (
        <div className="serving-adjuster">
          <span className="serving-adjuster-label">Adjust servings</span>
          <button className="serving-reset" onClick={() => setCurrentServings(recipe.baseServings)}
            style={{opacity: currentServings !== recipe.baseServings ? 1 : 0, pointerEvents: currentServings !== recipe.baseServings ? 'auto' : 'none', marginLeft: 8}}>
            Reset
          </button>
          <div className="serving-controls" style={{marginLeft: 'auto'}}>
            <button className="serving-btn" onClick={() => setCurrentServings(s => Math.max(1, s - 1))}>−</button>
            <span className="serving-count">{currentServings}</span>
            <button className="serving-btn" onClick={() => setCurrentServings(s => s + 1)}>+</button>
          </div>
        </div>
      )}

      {editing ? (
        /* ── Edit mode body ── */
        <div className="edit-form">
          <div>
            <div className="edit-section-header">
              <h2 className="section-title" style={{margin:0}}>Ingredients</h2>
            </div>
            <div className="edit-ingredients">
              {draft.ingredients.map((ing, i) => (
                <div key={i} className="edit-ingredient-row">
                  <input className="edit-input" placeholder="Amount" value={ing.amount} onChange={e => updateDraftIng(i,'amount',e.target.value)} />
                  <input className="edit-input" placeholder="Unit" value={ing.unit || ''} onChange={e => updateDraftIng(i,'unit',e.target.value)} />
                  <input className="edit-input" placeholder="Ingredient" value={ing.item} onChange={e => updateDraftIng(i,'item',e.target.value)} />
                  <button className="edit-row-remove" onClick={() => removeIng(i)} title="Remove">×</button>
                </div>
              ))}
            </div>
            <button className="edit-add-btn" onClick={addIng}><i className="ri-add-line" /> Add ingredient</button>
          </div>

          <div style={{marginTop:28}}>
            <div className="edit-section-header">
              <h2 className="section-title" style={{margin:0}}>Method</h2>
            </div>
            <div className="edit-steps">
              {draft.steps.map((step, i) => (
                <div key={i} className="edit-step-row">
                  <div className="edit-step-num">{i + 1}</div>
                  <textarea className="edit-textarea" value={step.instruction} onChange={e => updateDraftStep(i, e.target.value)} placeholder={`Step ${i + 1}`} style={{minHeight:64}} />
                  <button className="edit-row-remove" onClick={() => removeStep(i)} title="Remove" style={{marginTop:8}}>×</button>
                </div>
              ))}
            </div>
            <button className="edit-add-btn" onClick={addStep}><i className="ri-add-line" /> Add step</button>
          </div>

          <div style={{marginTop:24}}>
            <div className="edit-field">
              <label className="edit-label">Chef's notes (optional)</label>
              <textarea className="edit-textarea" value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} placeholder="Any extra tips or notes…" />
            </div>
          </div>

          <div className="edit-actions">
            <button className="btn-save" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
            <button className="btn-cancel-edit" onClick={cancelEdit}>Cancel</button>
          </div>
        </div>
      ) : (
        /* ── View mode body ── */
        <div className="recipe-body">
          {/* Ingredients */}
          <div>
            <h2 className="section-title">Ingredients</h2>
            <ul className="ingredient-list">
              {(recipe.ingredients || []).map((ing, i) => (
                <li key={i} className="ingredient-item">
                  <span className="ingredient-amount">
                    {[scaleIngredient(ing.amount, recipe.baseServings, currentServings), ing.unit].filter(Boolean).join(' ')}
                  </span>
                  <span>{ing.item}{ing.notes && <span className="ingredient-notes">, {ing.notes}</span>}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Steps */}
          <div>
            <h2 className="section-title">Method</h2>
            <ol className="step-list">
              {(recipe.steps || []).map((step, i) => (
                <li key={i} className="step-item">
                  <div className="step-number">{step.step || i + 1}</div>
                  <p className="step-text">{step.instruction}</p>
                </li>
              ))}
            </ol>
            {recipe.notes && (
              <div className="recipe-notes">
                <h4>Chef's Notes</h4>
                <p>{recipe.notes}</p>
              </div>
            )}
            {recipe.tags?.length > 0 && (
              <div className="tags-row">
                {recipe.tags.map((tag, i) => <span key={i} className="tag">{tag}</span>)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Footer: creator + actions ── */}
      <div className="recipe-footer">
        {(recipe.creatorUsername || recipe.creatorDisplayName) && (
          <CreatorStrip recipe={recipe} />
        )}
        <div className="action-bar" style={{marginTop: 12}}>
          <a href={recipe.instagramUrl} target="_blank" rel="noopener noreferrer" className="btn-instagram">
            View on Instagram
          </a>
          <button className="btn-delete" onClick={() => onDelete(recipe.id)}><IconTrash style={{fontSize:14}} /> Remove</button>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile Shell ──────────────────────────────────────────────────────────────

function MobileShell({ recipes, loading, mobileTab, setMobileTab, mobileSelectedId, setMobileSelectedId, mobileSearch, setMobileSearch, url, setUrl, status, handleAddRecipe, handleDelete, handleUpdatePhoto, handleUpdateRecipe, newRecipeId, setNewRecipeId, user, firstName, onCookMode, onSub, onShopping, onWcim }) {
  const selectedRecipe = recipes.find(r => r.id === mobileSelectedId);
  const [showAccountMenu, setShowAccountMenu] = React.useState(false);

  // Nav: Home · Recipes · [+] · Shopping · Inspire
  const tabs = [
    { id: 'home',     label: 'Home',     icon: 'home-4' },
    { id: 'recipes',  label: 'Recipes',  icon: 'book-2' },
    { id: 'add',      label: '',         icon: 'add',   add: true },
    { id: 'shopping', label: 'Shopping', icon: 'shopping-cart-2', action: onShopping },
    { id: 'inspire',  label: 'Inspire',  icon: 'sparkling-2', action: onWcim },
  ];

  function NavBar() {
    return (
      <div className="mobile-bottom-nav">
        {tabs.map(tab => (
          <button key={tab.id}
            className={`mobile-tab${tab.add ? ' add-tab' : ''}${!tab.action && !tab.add && mobileTab === tab.id ? ' active' : ''}`}
            onClick={() => {
              if (tab.action) { tab.action(); return; }
              setMobileSelectedId(null);
              setMobileTab(tab.id);
            }}>
            <span className="mobile-tab-icon"><i className={`ri-${tab.icon}-line`} /></span>
            {tab.label ? <span className="mobile-tab-label">{tab.label}</span> : null}
          </button>
        ))}
      </div>
    );
  }

  const initials = (user?.fullName || user?.firstName || '?').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();

  const header = (
    <div className="mobile-header">
      <div className="mobile-header-logo"><FullLogo height={20} /></div>
      <button onClick={() => setShowAccountMenu(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
        {user?.imageUrl
          ? <img src={user.imageUrl} alt="" style={{ width: 30, height: 30, borderRadius: '50%', border: '2px solid rgba(196,98,45,0.6)' }} />
          : <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--terracotta)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: 'white', fontFamily: "'DM Sans',sans-serif" }}>{initials}</div>
        }
      </button>
    </div>
  );

  if (selectedRecipe) {
    return (
      <div className="mobile-shell">
        {header}
        {showAccountMenu && <AccountMenu user={user} onClose={() => setShowAccountMenu(false)} />}
        <div className="mobile-content">
          <button onClick={() => setMobileSelectedId(null)}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 18px', background:'var(--ink)', border:'none', borderBottom:'1px solid rgba(255,255,255,0.07)', width:'100%', cursor:'pointer', position:'sticky', top:0, zIndex:10 }}>
            <i className="ri-arrow-left-line" style={{ color:'#faf7f2', fontSize:18 }} />
            <span style={{ color:'#faf7f2', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>Back</span>
          </button>
          <MobileDetail recipe={selectedRecipe} onBack={() => setMobileSelectedId(null)}
            onDelete={id => { handleDelete(id); setMobileSelectedId(null); }}
            onUpdatePhoto={handleUpdatePhoto} onUpdateRecipe={handleUpdateRecipe} onCookMode={onCookMode} onSub={onSub}
            isNew={newRecipeId === selectedRecipe?.id} onDismissNew={() => setNewRecipeId(null)} />
        </div>
        <NavBar />
      </div>
    );
  }

  return (
    <div className="mobile-shell">
      {/* Emoji font preloader */}
      <div className="emoji-preloader" aria-hidden="true">🍗🍔🍝🍰🍫🥗🍲🍕🌮🍣🍛🐟🍳🥣🥤🍞🥩🥓🥦🍽</div>
      {header}
      {showAccountMenu && <AccountMenu user={user} onClose={() => setShowAccountMenu(false)} />}
      <div className="mobile-content">
        {mobileTab === 'home' ? (
          loading ? null : (
            <MobileHomeScreen firstName={firstName} recipes={recipes}
              url={url} setUrl={setUrl} status={status} onImport={async () => {
                const recipeId = await handleAddRecipe();
                if (recipeId) { setMobileSelectedId(recipeId); setNewRecipeId(recipeId); }
              }}
              onSelectRecipe={setMobileSelectedId} />
          )
        ) : mobileTab === 'recipes' ? (
          <MobileSearch recipes={recipes} search={mobileSearch} setSearch={setMobileSearch} onSelect={setMobileSelectedId} showAll />
        ) : mobileTab === 'add' ? (
          <MobileAdd url={url} setUrl={setUrl} status={status} handleAddRecipe={handleAddRecipe} setMobileTab={setMobileTab} setMobileSelectedId={setMobileSelectedId} />
        ) : null}
      </div>
      <NavBar />
    </div>
  );
}

// ─── Mobile Home Screen ────────────────────────────────────────────────────────

function MobileHomeScreen({ firstName, recipes, url, setUrl, status, onImport, onSelectRecipe }) {
  const hasRecipes = recipes.length > 0;
  const recent = recipes.slice(0, 5);
  const isLoading = status?.type === 'loading';

  function handleImport() { onImport(); }

  const importBar = (
    <div className="mobile-import-bar">
      <input
        className="mobile-import-url" type="url"
        placeholder="Paste an Instagram reel URL…"
        value={url} onChange={e => setUrl(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleImport()}
        disabled={isLoading}
      />
      <button className="mobile-import-icon-btn" onClick={handleImport}
        disabled={isLoading || !url.trim()}>
        {isLoading ? <Ri name="loader-4" /> : <Ri name="arrow-up" />}
      </button>
    </div>
  );

  const statusEl = status && status.type !== 'loading' && (
    <div style={{ fontSize: 12, padding: '8px 14px', borderRadius: 100,
      background: status.type === 'error' ? '#fdf0f0' : '#eef3e8',
      color: status.type === 'error' ? '#c44040' : '#5a7a46' }}>
      {status.message}
    </div>
  );

  if (!hasRecipes) {
    return (
      <div className="mobile-home" style={{ justifyContent: 'center', flex: 1 }}>
        <div>
          <div className="mobile-home-eyebrow">Welcome to Reel Recipes</div>
          <div className="mobile-home-title">Save your first <em>recipe.</em></div>
        </div>
        <div className="mobile-home-sub">
          Paste any Instagram Reel link — we'll pull out the ingredients and steps automatically.
        </div>
        {importBar}
        {statusEl}
        <div className="mobile-home-hint">
          <i className="ri-instagram-line" style={{ fontSize: 13 }} />
          Works with any Instagram Reel
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-home">
      <div>
        <div className="mobile-home-eyebrow">{getTimeOfDay()}</div>
        <div className="mobile-home-title">Hey, <em>{firstName}.</em></div>
        <div style={{ fontSize: 13, color: 'var(--ink-muted)', marginTop: 5 }}>What are you cooking today?</div>
      </div>

      {importBar}
      {statusEl}

      {recent.length > 0 && (
        <>
          <div className="mobile-recent-label">Recently saved</div>
          <div className="mobile-recent-list">
            {recent.map(recipe => (
              <div key={recipe.id} className="mobile-recent-row" onClick={() => onSelectRecipe(recipe.id)}>
                <div className={`mobile-recent-thumb${recipe.thumbnailUrl ? ' has-image' : ''}`}>
                  {recipe.thumbnailUrl
                    ? <img src={proxyImage(recipe.thumbnailUrl)} alt="" onError={e => e.target.style.display='none'} />
                    : recipeEmoji(recipe)}
                </div>
                <div className="mobile-recent-info">
                  <div className="mobile-recent-title">{recipe.title}</div>
                  <div className="mobile-recent-meta">{[recipe.creatorUsername && `@${recipe.creatorUsername}`, recipe.totalTime].filter(Boolean).join(' · ')}</div>
                </div>
                <div className="mobile-recent-arrow"><i className="ri-arrow-right-s-line" /></div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MobileDetail({ recipe, onBack, onDelete, onUpdatePhoto, onUpdateRecipe, onCookMode, onSub, isNew, onDismissNew }) {
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);
  const [mobileServings, setMobileServings] = React.useState(recipe.baseServings || null);
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    setImgError(false);
    setMobileServings(recipe.baseServings || null);
    setEditing(false);
    setDraft(null);
  }, [recipe.id, recipe.thumbnailUrl]);

  function startEdit() {
    setDraft({
      title: recipe.title || '',
      description: recipe.description || '',
      prepTime: recipe.prepTime || '',
      cookTime: recipe.cookTime || '',
      totalTime: recipe.totalTime || '',
      servings: recipe.servings || '',
      ingredients: (recipe.ingredients || []).map(ing => ({ ...ing })),
      steps: (recipe.steps || []).map(s => ({ ...s })),
      notes: recipe.notes || '',
    });
    setEditing(true);
    if (onDismissNew) onDismissNew();
  }

  function cancelEdit() { setEditing(false); setDraft(null); }

  async function saveEdit() {
    if (!draft) return;
    setSaving(true);
    try {
      await onUpdateRecipe(recipe.id, {
        ...recipe,
        title: draft.title,
        description: draft.description,
        prepTime: draft.prepTime,
        cookTime: draft.cookTime,
        totalTime: draft.totalTime,
        servings: draft.servings,
        baseServings: recipe.baseServings,
        ingredients: draft.ingredients.filter(i => i.item?.trim()),
        steps: draft.steps.filter(s => s.instruction?.trim()).map((s, idx) => ({ ...s, step: idx + 1 })),
        notes: draft.notes,
      });
      setEditing(false);
      setDraft(null);
    } finally {
      setSaving(false);
    }
  }

  function updIng(idx, field, val) {
    setDraft(d => ({ ...d, ingredients: d.ingredients.map((ing, i) => i === idx ? { ...ing, [field]: val } : ing) }));
  }
  function addIng() { setDraft(d => ({ ...d, ingredients: [...d.ingredients, { amount: '', unit: '', item: '', notes: '' }] })); }
  function removeIng(idx) { setDraft(d => ({ ...d, ingredients: d.ingredients.filter((_, i) => i !== idx) })); }
  function updStep(idx, val) { setDraft(d => ({ ...d, steps: d.steps.map((s, i) => i === idx ? { ...s, instruction: val } : s) })); }
  function addStep() { setDraft(d => ({ ...d, steps: [...d.steps, { instruction: '', step: d.steps.length + 1 }] })); }
  function removeStep(idx) { setDraft(d => ({ ...d, steps: d.steps.filter((_, i) => i !== idx) })); }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          const maxSize = 1600;
          let { width, height } = img;
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(objectUrl);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = reject;
        img.src = objectUrl;
      });
      const res = await fetch(`${API_URL}/api/recipes/${recipe.id}/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onUpdatePhoto(recipe.id, data.thumbnailUrl);
    } catch (err) {
      alert('Failed to upload photo: ' + err.message);
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  }

  return (
    <div className="mobile-detail">
      <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handlePhotoUpload} />

      {/* Hero image — hidden in edit mode */}
      {!editing && (
        <div className={`mobile-detail-hero${(recipe.thumbnailUrl && !imgError) ? '' : ' no-image'}`} onClick={() => fileInputRef.current.click()} style={{ cursor: 'pointer' }}>
          {recipe.thumbnailUrl && !imgError ? (
            <img src={proxyImage(recipe.thumbnailUrl)} alt={recipe.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setImgError(true)} />
          ) : (
            <span className="mobile-detail-hero-emoji" style={{fontSize: '2.5rem'}}>{recipeEmoji(recipe)}</span>
          )}
          <div style={{ position: 'absolute', bottom: 8, right: 10, background: 'rgba(26,18,9,0.6)', borderRadius: 8, padding: '4px 10px', fontSize: 12, color: 'white' }}>
            {uploadingPhoto ? '⏳ Processing...' : (recipe.thumbnailUrl && !imgError) ? 'Change' : 'Add photo'}
          </div>
        </div>
      )}

      <div className="mobile-detail-body">

        {/* New recipe nudge banner */}
        {isNew && !editing && (
          <div className="edit-nudge" style={{marginBottom: 18}}>
            <span className="edit-nudge-text"><strong>Just imported!</strong> Does everything look right?</span>
            <button className="edit-nudge-btn" onClick={startEdit}>Review &amp; edit</button>
            <button className="edit-nudge-dismiss" onClick={onDismissNew}>×</button>
          </div>
        )}

        {editing ? (
          /* ══════════════ EDIT MODE ══════════════ */
          <div className="mobile-edit-form">

            {/* Title */}
            <div className="mobile-edit-field">
              <label className="edit-label">Title</label>
              <input className="mobile-edit-input mobile-edit-title" value={draft.title}
                onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="Recipe title" />
            </div>

            {/* Description */}
            <div className="mobile-edit-field">
              <label className="edit-label">Description</label>
              <textarea className="mobile-edit-textarea" value={draft.description}
                onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} placeholder="Short description (optional)" rows={2} />
            </div>

            {/* Time + serves grid */}
            <div className="mobile-edit-grid">
              {[['Serves','servings'],['Prep','prepTime'],['Cook','cookTime'],['Total','totalTime']].map(([label, key]) => (
                <div className="mobile-edit-field" key={key}>
                  <label className="edit-label">{label}</label>
                  <input className="mobile-edit-input" value={draft[key]}
                    onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} placeholder={label} />
                </div>
              ))}
            </div>

            {/* Ingredients */}
            <div className="mobile-edit-section-label">Ingredients</div>
            {draft.ingredients.map((ing, i) => (
              <div key={i} className="mobile-edit-ing-row">
                <div className="mobile-edit-ing-top">
                  <input className="mobile-edit-input mobile-edit-ing-amount" value={ing.amount}
                    onChange={e => updIng(i,'amount',e.target.value)} placeholder="Qty" />
                  <input className="mobile-edit-input mobile-edit-ing-unit" value={ing.unit || ''}
                    onChange={e => updIng(i,'unit',e.target.value)} placeholder="Unit" />
                  <button className="mobile-edit-remove" onClick={() => removeIng(i)}>×</button>
                </div>
                <input className="mobile-edit-input" value={ing.item}
                  onChange={e => updIng(i,'item',e.target.value)} placeholder="Ingredient name" />
              </div>
            ))}
            <button className="edit-add-btn" onClick={addIng} style={{marginBottom:24}}><i className="ri-add-line" /> Add ingredient</button>

            {/* Steps */}
            <div className="mobile-edit-section-label">Method</div>
            {draft.steps.map((step, i) => (
              <div key={i} className="mobile-edit-step-row">
                <div className="edit-step-num">{i + 1}</div>
                <textarea className="mobile-edit-textarea mobile-edit-step-text" value={step.instruction}
                  onChange={e => updStep(i, e.target.value)} placeholder={`Step ${i + 1}…`} rows={3} />
                <button className="mobile-edit-remove" onClick={() => removeStep(i)} style={{marginTop:4}}>×</button>
              </div>
            ))}
            <button className="edit-add-btn" onClick={addStep} style={{marginBottom:24}}><i className="ri-add-line" /> Add step</button>

            {/* Notes */}
            <div className="mobile-edit-field">
              <label className="edit-label">Chef's notes</label>
              <textarea className="mobile-edit-textarea" value={draft.notes}
                onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} placeholder="Any extra tips…" rows={3} />
            </div>

            {/* Save / Cancel */}
            <div className="mobile-edit-actions">
              <button className="btn-save" style={{flex:1, fontSize:15, padding:'13px 0'}} onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              <button className="btn-cancel-edit" style={{padding:'13px 20px', fontSize:15}} onClick={cancelEdit}>Cancel</button>
            </div>

          </div>
        ) : (
          /* ══════════════ VIEW MODE ══════════════ */
          <>
            <h1 className="mobile-detail-title">{recipe.title}</h1>
            {recipe.description && <p className="mobile-detail-desc">{recipe.description}</p>}

            <div className="mobile-meta-row">
              {recipe.servings && <div className="mobile-meta-pill"><div className="mobile-meta-label">Serves</div><div className="mobile-meta-value">{mobileServings || recipe.servings}</div></div>}
              {recipe.prepTime && <div className="mobile-meta-pill"><div className="mobile-meta-label">Prep</div><div className="mobile-meta-value">{recipe.prepTime}</div></div>}
              {recipe.cookTime && <div className="mobile-meta-pill"><div className="mobile-meta-label">Cook</div><div className="mobile-meta-value">{recipe.cookTime}</div></div>}
              {recipe.totalTime && <div className="mobile-meta-pill"><div className="mobile-meta-label">Total</div><div className="mobile-meta-value">{recipe.totalTime}</div></div>}
            </div>

            {recipe.baseServings && (
              <div className="mobile-serving-adjuster">
                <span style={{ fontSize:13, color:'var(--ink-muted)', fontWeight:500 }}>Adjust servings</span>
                <button className="serving-reset" onClick={() => setMobileServings(recipe.baseServings)}
                  style={{opacity: mobileServings !== recipe.baseServings ? 1 : 0, pointerEvents: mobileServings !== recipe.baseServings ? 'auto' : 'none', marginLeft: 8}}>
                  Reset
                </button>
                <div style={{ display:'flex', alignItems:'center', gap:14, marginLeft:'auto' }}>
                  <button className="serving-btn" onClick={() => setMobileServings(s => Math.max(1, s - 1))}>−</button>
                  <span className="serving-count">{mobileServings}</span>
                  <button className="serving-btn" onClick={() => setMobileServings(s => s + 1)}>+</button>
                </div>
              </div>
            )}

            {/* Action buttons row */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
              {onCookMode && <button className="btn-cook-mode" style={{ flex: 1, justifyContent: 'center', fontSize: '0.82rem', padding: '9px 14px' }} onClick={() => onCookMode(recipe)}><IconCook style={{fontSize:15}} /> Cook Mode</button>}
              {onSub && <button className="btn-toolbar" style={{ flex: 1, justifyContent: 'center', fontSize: '0.82rem' }} onClick={() => onSub(recipe)}><IconRefresh style={{fontSize:14}} /> Substitutions</button>}
              <button className="btn-edit" style={{ flex: 1, justifyContent: 'center', fontSize: '0.82rem' }} onClick={startEdit}>
                <i className="ri-pencil-line" style={{fontSize:13}} /> Edit
              </button>
            </div>

            <div className="mobile-section-title">Ingredients</div>
            {(recipe.ingredients || []).map((ing, i) => (
              <div key={i} className="mobile-ingredient">
                <span className="mobile-ingredient-amount">{[scaleIngredient(ing.amount, recipe.baseServings, mobileServings), ing.unit].filter(Boolean).join(' ')}</span>
                <span>{ing.item}{ing.notes ? <span style={{ color: '#9a8f80', fontStyle: 'italic' }}>, {ing.notes}</span> : null}</span>
              </div>
            ))}

            <div className="mobile-section-title">Method</div>
            {(recipe.steps || []).map((step, i) => (
              <div key={i} className="mobile-step">
                <div className="mobile-step-num">{step.step || i + 1}</div>
                <p className="mobile-step-text">{step.instruction}</p>
              </div>
            ))}

            {recipe.notes && (
              <div className="mobile-notes">
                <div className="mobile-notes-label">Chef's Notes</div>
                <p className="mobile-notes-text">{recipe.notes}</p>
              </div>
            )}

            {recipe.tags?.length > 0 && (
              <div className="mobile-tags">
                {recipe.tags.map((tag, i) => <span key={i} className="mobile-tag">{tag}</span>)}
              </div>
            )}

            <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
              {(recipe.creatorUsername || recipe.creatorDisplayName) && (
                <CreatorStrip recipe={recipe} mobile />
              )}
              <div className="mobile-action-row" style={{ marginTop: 12 }}>
                <a href={recipe.instagramUrl} target="_blank" rel="noopener noreferrer" className="mobile-btn-ig" style={{display:"flex",alignItems:"center",gap:6}}><IconInstagram style={{fontSize:15}} /> View on Instagram</a>
                <button className="mobile-btn-delete" onClick={() => onDelete(recipe.id)}><IconTrash style={{fontSize:14}} /> Remove</button>
              </div>
            </div>

            <div style={{ height: 32 }} />
          </>
        )}
      </div>
    </div>
  );
}

function MobileAdd({ url, setUrl, status, handleAddRecipe, setMobileTab, setMobileSelectedId }) {
  async function onAdd() {
    const recipeId = await handleAddRecipe();
    if (recipeId) {
      // Navigate straight to the new recipe
      setMobileSelectedId(recipeId);
      setMobileTab('home');
    }
  }
  return (
    <div className="mobile-add-tab">
      <div className="mobile-add-title">Add Recipe</div>
      <div className="mobile-add-subtitle">Paste an Instagram reel or post URL</div>
      <input
        className="mobile-url-input"
        type="url"
        placeholder="https://instagram.com/reel/..."
        value={url}
        onChange={e => setUrl(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onAdd()}
        disabled={status?.type === 'loading'}
      />
      {status?.type === 'error' && (
        <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13, background: '#fdf0f0', color: '#c44040' }}>
          {status.message}
        </div>
      )}
      <button className="mobile-btn-add" onClick={onAdd} disabled={status?.type === 'loading' || !url.trim()}>
        {status?.type === 'loading' ? 'Importing…' : 'Extract Recipe'}
      </button>
      <div className="mobile-add-hint">
        <div className="mobile-add-hint-title">How it works</div>
        <div className="mobile-add-hint-text">We read the caption for measurements. If none are found, the video is automatically transcribed.</div>
      </div>
    </div>
  );
}

// ─── Account Menu ─────────────────────────────────────────────────────────────

function useAccountActions(onClose) {
  async function handleSignOut() {
    onClose();
    await window.Clerk?.signOut();
  }
  async function handleDeleteAccount() {
    onClose();
    const confirmed = window.confirm(
      'Delete your account? This will permanently remove all your saved recipes and cannot be undone.'
    );
    if (!confirmed) return;
    try {
      await fetch(`${window.API_URL || ''}/api/recipes/all`, { method: 'DELETE' }).catch(() => {});
      await window.Clerk?.user?.delete();
    } catch (e) {
      alert('Could not delete account. Please contact support.');
    }
  }
  return { handleSignOut, handleDeleteAccount };
}

// Mobile bottom-sheet variant
function AccountMenu({ user, onClose }) {
  const name = user?.fullName || user?.firstName || 'You';
  const email = user?.primaryEmailAddress?.emailAddress || '';
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const { handleSignOut, handleDeleteAccount } = useAccountActions(onClose);

  return (
    <div className="account-menu-overlay">
      <div className="account-menu-backdrop" onClick={onClose} />
      <div className="account-menu-sheet">
        <div className="account-menu-header">
          {user?.imageUrl
            ? <img src={user.imageUrl} alt="" className="account-menu-avatar" />
            : <div className="account-menu-avatar-placeholder">{initials}</div>
          }
          <div>
            <div className="account-menu-name">{name}</div>
            {email && <div className="account-menu-email">{email}</div>}
          </div>
        </div>
        <button className="account-menu-item" onClick={handleSignOut}>
          <div className="account-menu-item-icon logout"><i className="ri-logout-box-r-line" /></div>
          <div>
            <div className="account-menu-item-text">Sign out</div>
            <div className="account-menu-item-sub">Sign out of your account</div>
          </div>
        </button>
        <button className="account-menu-item" onClick={handleDeleteAccount}>
          <div className="account-menu-item-icon danger"><i className="ri-delete-bin-line" /></div>
          <div>
            <div className="account-menu-item-text danger">Delete account</div>
            <div className="account-menu-item-sub">Permanently delete all data</div>
          </div>
        </button>
      </div>
    </div>
  );
}

// Desktop popover variant — sits above the nav footer
function AccountPopover({ user, onClose, anchorRef }) {
  const name = user?.fullName || user?.firstName || 'You';
  const email = user?.primaryEmailAddress?.emailAddress || '';
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const { handleSignOut, handleDeleteAccount } = useAccountActions(onClose);
  const popRef = React.useRef(null);

  // Calculate position from anchor
  const [pos, setPos] = React.useState({ left: 8, bottom: 80 });
  React.useEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ left: rect.left, bottom: window.innerHeight - rect.top + 8 });
    }
  }, [anchorRef]);

  // Click-outside to close
  React.useEffect(() => {
    function onDown(e) {
      if (popRef.current && !popRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose, anchorRef]);

  return (
    <div className="account-popover" ref={popRef} style={{ left: pos.left, bottom: pos.bottom }}>
      <div className="account-popover-header">
        {user?.imageUrl
          ? <img src={user.imageUrl} alt="" className="account-popover-avatar" />
          : <div className="account-popover-avatar-placeholder">{initials}</div>
        }
        <div style={{ minWidth: 0 }}>
          <div className="account-popover-name">{name}</div>
          {email && <div className="account-popover-email">{email}</div>}
        </div>
      </div>
      <button className="account-popover-item" onClick={handleSignOut}>
        <i className="ri-logout-box-r-line account-popover-item-icon" />
        <span className="account-popover-item-label">Sign out</span>
      </button>
      <button className="account-popover-item danger" onClick={handleDeleteAccount}>
        <i className="ri-delete-bin-line account-popover-item-icon" />
        <span className="account-popover-item-label">Delete account</span>
      </button>
    </div>
  );
}

function MobileSearch({ recipes, search, setSearch, onSelect }) {
  const results = search
    ? recipes.filter(r => r.title?.toLowerCase().includes(search.toLowerCase()) || (r.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase())) || (r.ingredients || []).some(i => i.item?.toLowerCase().includes(search.toLowerCase())))
    : recipes;

  return (
    <div className="mobile-search-tab">
      <div className="mobile-search-wrapper">
        <span className="mobile-search-icon"><IconSearch /></span>
        <input
          className="mobile-search-input"
          placeholder="Search by name, ingredient, tag..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      {search && <div style={{ fontSize: 12, color: '#9a8f80', marginBottom: 12 }}>{results.length} result{results.length !== 1 ? 's' : ''}</div>}
      {results.map(recipe => (
        <div key={recipe.id} className="mobile-recipe-item" onClick={() => onSelect(recipe.id)} style={{ borderRadius: 10, marginBottom: 4 }}>
          <div className="mobile-recipe-emoji">{recipeEmoji(recipe)}</div>
          <div className="mobile-recipe-info">
            <div className="mobile-recipe-title">{recipe.title}</div>
            {recipe.totalTime && <div className="mobile-recipe-time">{recipe.totalTime}</div>}
          </div>
          <span className="mobile-recipe-arrow"><IconChevronRight /></span>
        </div>
      ))}
    </div>
  );
}

// ─── Desktop Creator List ──────────────────────────────────────────────────────

function DesktopCreatorList({ creators, recipes, selectedId, onSelect }) {
  const [expanded, setExpanded] = React.useState(null);

  if (creators.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-muted)', fontSize: '0.85rem' }}>No creators yet.</div>;
  }

  return (
    <div>
      {creators.map(creator => (
        <div key={creator.username || 'unknown'}>
          <div className="creator-card" onClick={() => setExpanded(expanded === creator.username ? null : creator.username)}>
            <CreatorAvatar displayName={creator.displayName} username={creator.username} storedUrl={creator.avatarUrl} size={44} />
            <div className="creator-card-info">
              <div className="creator-card-name">{creator.displayName || creator.username || 'Unknown'}</div>
              <div className="creator-card-count">{creator.recipes.length} recipe{creator.recipes.length !== 1 ? 's' : ''}</div>
            </div>
            <span style={{ color: 'var(--terracotta)', fontSize: 16, transition: 'transform 0.2s', transform: expanded === creator.username ? 'rotate(90deg)' : 'none' }}><IconChevronRight /></span>
          </div>
          {expanded === creator.username && (
            <div className="creator-recipe-indent">
              {creator.recipes.map(recipe => (
                <div key={recipe.id} className={`recipe-card ${selectedId === recipe.id ? 'active' : ''}`}
                  style={{ paddingLeft: 12 }} onClick={() => onSelect(recipe.id)}>
                  <RecipeThumb recipe={recipe} />
                  <div className="recipe-card-info">
                    <div className="recipe-card-title">{recipe.title}</div>
                    <div className="recipe-card-meta">{recipe.totalTime && <span>{recipe.totalTime}</span>}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Mobile Creator List ───────────────────────────────────────────────────────

function MobileCreatorList({ creators, onSelect }) {
  const [expanded, setExpanded] = React.useState(null);

  if (creators.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9a8f80' }}>
        <div style={{ marginBottom: 16, color: "var(--ink-muted)" }}><IconUsers /></div>
        <p style={{ fontSize: 14 }}>Add recipes to see creators here.</p>
      </div>
    );
  }

  return (
    <div>
      {creators.map(creator => (
        <div key={creator.username || 'unknown'}>
          <div className="mobile-creator-item" onClick={() => setExpanded(expanded === creator.username ? null : creator.username)}>
            <CreatorAvatar displayName={creator.displayName} username={creator.username} storedUrl={creator.avatarUrl} size={52} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mobile-creator-name">{creator.displayName || creator.username || 'Unknown'}</div>
              {creator.username && <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>@{creator.username}</div>}
              <div className="mobile-creator-count">{creator.recipes.length} recipe{creator.recipes.length !== 1 ? 's' : ''}</div>
            </div>
            <span style={{ color: 'var(--terracotta)', fontSize: 20, transition: 'transform 0.2s', transform: expanded === creator.username ? 'rotate(90deg)' : 'none' }}><IconChevronRight /></span>
          </div>

          {expanded === creator.username && (
            <div style={{ background: 'var(--cream)' }}>
              {creator.recipes.map(recipe => (
                <div key={recipe.id} className="mobile-recipe-item" style={{ paddingLeft: 28 }} onClick={() => onSelect(recipe.id)}>
                  <div className="mobile-recipe-emoji" style={{ width: 44, height: 44, fontSize: 20 }}>{recipeEmoji(recipe)}</div>
                  <div className="mobile-recipe-info">
                    <div className="mobile-recipe-title" style={{ fontSize: 14 }}>{recipe.title}</div>
                    {recipe.totalTime && <div className="mobile-recipe-time">{recipe.totalTime}</div>}
                  </div>
                  <span className="mobile-recipe-arrow"><IconChevronRight /></span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Cook Mode ─────────────────────────────────────────────────────────────────

function CookMode({ recipe, onClose }) {
  const steps = recipe.steps || [];
  const [stepIdx, setStepIdx] = React.useState(0);
  const done = stepIdx >= steps.length;

  React.useEffect(() => {
    // Keep screen awake if supported
    let wakeLock = null;
    if ('wakeLock' in navigator) {
      navigator.wakeLock.request('screen').then(wl => { wakeLock = wl; }).catch(() => {});
    }
    return () => { wakeLock?.release?.(); };
  }, []);

  // Find ingredients mentioned in this step
  function relevantIngredients(stepText) {
    if (!stepText || !recipe.ingredients) return [];
    const text = stepText.toLowerCase();
    return (recipe.ingredients || []).filter(ing =>
      ing.item && text.includes(ing.item.toLowerCase().split(' ')[0])
    );
  }

  const currentStep = steps[stepIdx];
  const relIng = currentStep ? relevantIngredients(currentStep.instruction) : [];
  const progress = done ? 100 : (stepIdx / steps.length) * 100;

  return (
    <div className="cook-mode-overlay">
      <div className="cook-mode-header">
        <div className="cook-mode-title">{recipe.title}</div>
        <button className="cook-mode-exit" onClick={onClose}><IconX /> Exit</button>
      </div>
      <div className="cook-mode-progress">
        <div className="cook-mode-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {done ? (
        <div className="cook-mode-done-screen">
          <div className="cook-mode-done-emoji">🎉</div>
          <div className="cook-mode-done-title">All done!</div>
          <div className="cook-mode-done-sub">{recipe.title} is ready. Enjoy!</div>
          <button className="cook-mode-exit" style={{ marginTop: 24, padding: '10px 28px', fontSize: '0.9rem' }} onClick={onClose}>
            Back to recipe
          </button>
        </div>
      ) : (
        <>
          <div className="cook-mode-body">
            <div className="cook-mode-step-label">Step {stepIdx + 1} of {steps.length}</div>
            <div className="cook-mode-step-text">{currentStep?.instruction}</div>

            {relIng.length > 0 && (
              <div className="cook-mode-ingredients">
                <div className="cook-mode-ing-label">You'll need</div>
                {relIng.map((ing, i) => (
                  <div key={i} className="cook-mode-ing-row">
                    <span className="cook-mode-ing-amount">{[ing.amount, ing.unit].filter(Boolean).join(' ')}</span>
                    <span>{ing.item}{ing.notes ? `, ${ing.notes}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="cook-mode-nav">
            <button className="cook-mode-nav-btn" onClick={() => setStepIdx(i => i - 1)} disabled={stepIdx === 0}><IconArrowLeft /></button>
            <span className="cook-mode-counter">{stepIdx + 1} / {steps.length}</span>
            <button className="cook-mode-nav-btn" onClick={() => setStepIdx(i => i + 1)}>→</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Shopping List ──────────────────────────────────────────────────────────────

function buildShoppingList(recipes, selectedIds) {
  const selected = recipes.filter(r => selectedIds.has(r.id));
  const merged = {};

  selected.forEach(recipe => {
    (recipe.ingredients || []).forEach(ing => {
      if (!ing.item) return;
      const key = ing.item.toLowerCase().trim();
      if (!merged[key]) {
        merged[key] = { item: ing.item, amounts: [], unit: ing.unit || '' };
      }
      if (ing.amount) {
        merged[key].amounts.push(`${ing.amount}${ing.unit ? ' ' + ing.unit : ''}`);
      }
    });
  });

  return Object.values(merged).sort((a, b) => a.item.localeCompare(b.item));
}

function ShoppingList({ recipes, onClose, darkMode }) {
  const [selectedIds, setSelectedIds] = React.useState(new Set(recipes.map(r => r.id)));
  const [checked, setChecked] = React.useState(new Set());
  const list = buildShoppingList(recipes, selectedIds);

  function toggleRecipe(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleCheck(item) {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(item) ? next.delete(item) : next.add(item);
      return next;
    });
  }

  function shareList() {
    const text = list
      .map(i => `${checked.has(i.item) ? '✓' : '○'} ${i.amounts.length ? i.amounts.join(' + ') + '  ' : ''}${i.item}`)
      .join('\n');
    const header = `Shopping List\n${[...selectedIds].map(id => recipes.find(r => r.id === id)?.title).filter(Boolean).join(', ')}\n\n`;
    if (navigator.share) {
      navigator.share({ title: 'Shopping List', text: header + text });
    } else {
      navigator.clipboard.writeText(header + text);
      alert('Copied to clipboard!');
    }
  }

  const unchecked = list.filter(i => !checked.has(i.item));
  const checkedItems = list.filter(i => checked.has(i.item));

  return (
    <div className="shopping-panel">
      <div className="shopping-backdrop" onClick={onClose} />
      <div className={`shopping-drawer${darkMode ? " dark-mode" : ""}`}>
        <div className="shopping-header">
          <IconCart />
          <div className="shopping-title">Shopping List</div>
          <button className="shopping-close" onClick={onClose}><IconX /></button>
        </div>

        <div className="shopping-body">
          {/* Recipe selector chips */}
          <div className="shopping-section-label">Recipes</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 4 }}>
            {recipes.map(r => (
              <div key={r.id} className={`shopping-recipe-chip ${selectedIds.has(r.id) ? 'selected' : ''}`}
                onClick={() => toggleRecipe(r.id)}>
                {selectedIds.has(r.id) && <span style={{fontSize:11,color:"white"}}><IconCheck /></span>}
                {r.title}
              </div>
            ))}
          </div>

          {selectedIds.size === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ink-muted)', fontSize: '0.88rem' }}>
              Select recipes above to build your list
            </div>
          ) : (
            <>
              <div className="shopping-section-label">
                {unchecked.length} item{unchecked.length !== 1 ? 's' : ''} remaining
              </div>
              {unchecked.map(ing => (
                <div key={ing.item} className="shopping-item" onClick={() => toggleCheck(ing.item)}>
                  <div className="shopping-check" />
                  <div className="shopping-item-amount">{ing.amounts.length ? ing.amounts.join(' + ') : ''}</div>
                  <div className="shopping-item-name">{ing.item}</div>
                </div>
              ))}

              {checkedItems.length > 0 && (
                <>
                  <div className="shopping-section-label" style={{ marginTop: 24 }}>In the basket</div>
                  {checkedItems.map(ing => (
                    <div key={ing.item} className="shopping-item checked" onClick={() => toggleCheck(ing.item)}>
                      <div className="shopping-check" style={{fontSize:13}}><IconCheck /></div>
                      <div className="shopping-item-amount">{ing.amounts.length ? ing.amounts.join(' + ') : ''}</div>
                      <div className="shopping-item-name">{ing.item}</div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div className="shopping-footer">
          <button className="shopping-btn-share" onClick={shareList} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7}}><IconShare style={{fontSize:16}} /> Share list</button>
          <button className="shopping-btn-clear" onClick={() => setChecked(new Set())}>Reset</button>
        </div>
      </div>
    </div>
  );
}

// ─── AI Chat Panel (shared for Sub Assistant + What Can I Make) ────────────────

function AiChatPanel({ config, onClose, darkMode }) {
  const [messages, setMessages] = React.useState(config.initialMessages || []);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [ingredients, setIngredients] = React.useState([]); // for WCIM mode
  const [ingInput, setIngInput] = React.useState('');
  const bodyRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const drawerRef = React.useRef(null);

  // Reset Safari zoom when panel unmounts
  React.useEffect(() => {
    return () => {
      // Force Safari to reset zoom after keyboard dismissal
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        const original = viewport.content;
        viewport.content = 'width=device-width, initial-scale=1, maximum-scale=1';
        setTimeout(() => { viewport.content = original; }, 100);
      }
    };
  }, []);

  React.useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  // Auto-trigger initial AI message if config says to
  React.useEffect(() => {
    if (config.autoSend) sendMessage(config.autoSend, true);
  }, []);

  async function sendMessage(text, silent = false) {
    if (!text.trim() && ingredients.length === 0) return;
    const userText = text.trim() || (config.mode === 'wcim' ? `I have: ${ingredients.join(', ')}` : '');
    if (!silent) {
      setMessages(prev => [...prev, { role: 'user', content: userText }]);
    }
    setInput('');
    setLoading(true);

    try {
      const history = [...messages, { role: 'user', content: userText }];
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: config.systemPrompt,
          messages: history.map(m => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'AI request failed');
      const reply = data.content?.[0]?.text || 'Hmm, something went wrong. Try again!';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch(e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Check your connection and try again.' }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  }

  // WCIM ingredient tag input
  function handleIngKeyDown(e) {
    if ((e.key === 'Enter' || e.key === ',') && ingInput.trim()) {
      e.preventDefault();
      setIngredients(prev => [...prev, ingInput.trim().replace(/,$/, '')]);
      setIngInput('');
    } else if (e.key === 'Backspace' && !ingInput && ingredients.length > 0) {
      setIngredients(prev => prev.slice(0, -1));
    }
  }

  function removeIngredient(i) {
    setIngredients(prev => prev.filter((_, idx) => idx !== i));
  }

  function submitWcim() {
    if (ingredients.length === 0) return;
    const text = `I have these ingredients: ${ingredients.join(', ')}. What can I make?`;
    sendMessage(text);
    setIngredients([]);
  }

  return (
    <div className={`ai-panel${darkMode ? " dark-mode" : ""}`}>
      <div className="ai-backdrop" onClick={onClose} />
      <div className="ai-drawer" ref={drawerRef}>
        <div className="ai-header">
          <div className="ai-header-icon">{config.icon === 'sub' ? <IconRefresh /> : config.icon === 'wcim' ? <IconSparkles /> : config.icon}</div>
          <div className="ai-header-text">
            <div className="ai-header-title">{config.title}</div>
            <div className="ai-header-sub">{config.subtitle}</div>
          </div>
          <button className="ai-close" onClick={onClose}><IconX /></button>
        </div>

        <div className="ai-body" ref={bodyRef}>
          {messages.length === 0 && config.emptyState}

          {messages.map((msg, i) => (
            <div key={i} className={`ai-msg ${msg.role}`}
              dangerouslySetInnerHTML={{ __html: msg.content.replace(/\n/g, '<br/>').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
          ))}

          {loading && (
            <div className="ai-msg thinking">
              {config.thinkingText || 'Thinking'}
              <span className="ai-typing-dots"><span /><span /><span /></span>
            </div>
          )}
        </div>

        {/* Suggestion chips for follow-ups */}
        {messages.length > 0 && !loading && config.suggestions && (
          <div style={{ padding: '0 24px', paddingBottom: 8 }}>
            <div className="ai-suggestion-chips">
              {config.suggestions.map((s, i) => (
                <div key={i} className="ai-chip" onClick={() => sendMessage(s)}>{s}</div>
              ))}
            </div>
          </div>
        )}

        <div className="ai-footer">
          {config.mode === 'wcim' && messages.length === 0 ? (
            <div>
              <div style={{ fontSize: '0.78rem', color: '#9a8f80', fontFamily: 'DM Sans', marginBottom: 10 }}>
                Add your ingredients (press Enter or comma after each):
              </div>
              <div className="wcim-ingredients" onClick={() => document.getElementById('wcim-input')?.focus()}>
                {ingredients.map((ing, i) => (
                  <div key={i} className="wcim-tag">
                    {ing}
                    <span className="wcim-tag-remove" onClick={e => { e.stopPropagation(); removeIngredient(i); }}>×</span>
                  </div>
                ))}
                <input id="wcim-input" className="wcim-input"
                  placeholder={ingredients.length === 0 ? 'chicken, rice, garlic...' : 'add more...'}
                  value={ingInput} onChange={e => setIngInput(e.target.value)} onKeyDown={handleIngKeyDown} />
              </div>
              <button className="shopping-btn-share" style={{ marginTop: 12, width: '100%', opacity: ingredients.length === 0 ? 0.4 : 1 }}
                onClick={submitWcim} disabled={ingredients.length === 0}>
                ✨ Find recipes I can make
              </button>
            </div>
          ) : (
            <div className="ai-input-row">
              <input ref={inputRef} className="ai-input"
                placeholder={config.inputPlaceholder || 'Ask anything...'}
                value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown} disabled={loading} />
              <button className="ai-send" onClick={() => sendMessage(input)} disabled={loading || !input.trim()}>↑</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AI Config Builders ─────────────────────────────────────────────────────────

function buildSubConfig(recipe) {
  return {
    icon: 'sub',
    title: 'Substitution Assistant',
    subtitle: `Swapping ingredients in ${recipe.title}`,
    mode: 'sub',
    thinkingText: 'Checking the pantry',
    inputPlaceholder: "e.g. I don't have tahini...",
    systemPrompt: `You are a warm, witty sous chef helping someone cook "${recipe.title}". 
The recipe has these ingredients: ${(recipe.ingredients || []).map(i => [i.amount, i.unit, i.item].filter(Boolean).join(' ')).join(', ')}.
When someone asks about a substitution, give a fun, practical answer. 
Be conversational and encouraging. Use a little personality — like a friend in the kitchen, not a textbook.
Keep answers concise. Suggest 1-2 best substitutes with a brief note on how it'll affect the dish.
Format substitutions clearly with **bold** for the substitute name.`,
    suggestions: ["What if I'm out of eggs?", "Any dairy-free swaps?", "Can I make this vegan?", "What can I use instead of butter?"],
    emptyState: (
      <div style={{ textAlign: 'center', padding: '20px 0', color: '#9a8f80' }}>
        <div style={{ marginBottom: 12, color: "var(--terracotta)", display:"flex", justifyContent:"center", fontSize:48 }}><i className="ri-restaurant-line" style={{fontSize:48, lineHeight:1}} /></div>
        <div style={{ fontFamily: 'Playfair Display', fontSize: '1.1rem', color: '#c8bfb0', marginBottom: 8 }}>Missing something?</div>
        <div style={{ fontSize: '0.82rem', lineHeight: 1.7 }}>Tell me what you're missing or want to swap<br/>and I'll find you the perfect substitute.</div>
      </div>
    ),
  };
}

function buildWcimConfig(recipes) {
  const recipeList = recipes.map(r => `- "${r.title}" (ingredients: ${(r.ingredients||[]).slice(0,6).map(i=>i.item).join(', ')})`).join('\n');
  return {
    icon: 'wcim',
    title: "What Can I Make?",
    subtitle: 'Search your collection by ingredient',
    mode: 'wcim',
    thinkingText: 'Raiding your fridge',
    inputPlaceholder: 'Ask a follow-up...',
    systemPrompt: `You are a playful, enthusiastic kitchen companion helping someone figure out what to cook.
They have a recipe collection containing:
${recipeList}

When they tell you what ingredients they have, look through their collection and suggest the best matches.
Be enthusiastic and fun. Lead with the best match. Explain WHY each recipe works with what they have.
If they're missing a couple of ingredients for a good match, mention it and suggest easy swaps.
Format recipe names in **bold**. Keep it conversational — like a friend who's excited about food.
If nothing matches well, suggest they're close to something and what one ingredient would unlock it.`,
    suggestions: ["What needs the fewest extra ingredients?", "Something quick?", "What about breakfast?", "Most impressive dish?"],
    emptyState: null,
  };
}
